import React, { useState, useEffect, useCallback } from 'react';
import {
  Shield,
  LayoutDashboard,
  Users,
  BookOpen,
  MessageSquare,
  Radio,
  MessageCircle,
  Settings,
  Search,
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Trash2,
  Edit3,
  Sparkles,
  Mail,
  Ban,
  Clock,
  UserCheck,
  RotateCcw,
  KeyRound,
  ChevronLeft,
  ChevronRight,
  ArrowUpDown,
  X,
  Eye,
  Flag,
  Trophy,
  Activity,
  Lock,
  ArrowLeft,
} from 'lucide-react';
import { getSupabaseClient } from '../lib/supabase.ts';

export type AdminTab =
  | 'overview'
  | 'question-bank'
  | 'users'
  | 'content'
  | 'feedback'
  | 'events'
  | 'feed'
  | 'settings';

interface AdminPanelViewProps {
  adminUid: string;
  adminEmail: string;
  onExitAdmin: () => void;
  onPlatformSettingsChange?: (settings: {
    maintenanceMode: boolean;
    announcementBanner: string;
    signupsEnabled: boolean;
  }) => void;
  showToast: (msg: string, type?: 'info' | 'success' | 'error') => void;
}

export const AdminPanelView: React.FC<AdminPanelViewProps> = ({
  adminUid,
  adminEmail,
  onExitAdmin,
  onPlatformSettingsChange,
  showToast,
}) => {
  const [activeTab, setActiveTab] = useState<AdminTab>('overview');
  const [loading, setLoading] = useState(false);

  // Overview state
  const [dashboard, setDashboard] = useState<any>(null);

  // Users state
  const [usersData, setUsersData] = useState<{
    users: any[];
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
    countries: string[];
  }>({
    users: [],
    total: 0,
    page: 1,
    pageSize: 12,
    totalPages: 1,
    countries: [],
  });
  const [userSearch, setUserSearch] = useState('');
  const [userCountryFilter, setUserCountryFilter] = useState('ALL');
  const [userStatusFilter, setUserStatusFilter] = useState('ALL');
  const [userJoinedFilter, setUserJoinedFilter] = useState('ALL');
  const [userSortBy, setUserSortBy] = useState('joinedAt');
  const [userSortDir, setUserSortDir] = useState<'asc' | 'desc'>('desc');
  const [userPage, setUserPage] = useState(1);

  // Selected user drawer
  const [selectedUserUid, setSelectedUserUid] = useState<string | null>(null);
  const [userDetail, setUserDetail] = useState<any>(null);
  const [loadingUserDetail, setLoadingUserDetail] = useState(false);

  // Action Modals
  const [actionModal, setActionModal] = useState<{
    type:
      | 'ban'
      | 'unban'
      | 'suspend'
      | 'role'
      | 'reset_stats'
      | 'delete_user'
      | 'reset_password'
      | null;
    targetUser: any | null;
  }>({ type: null, targetUser: null });
  const [actionReason, setActionReason] = useState('');
  const [suspendDays, setSuspendDays] = useState(7);
  const [targetRole, setTargetRole] = useState<'user' | 'admin'>('user');
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [submittingAction, setSubmittingAction] = useState(false);

  // Content state
  const [contentSubTab, setContentSubTab] = useState<'reported' | 'bank'>('reported');
  const [contentData, setContentData] = useState<{
    reportedQueue: any[];
    questionBank: any[];
    categories: string[];
  }>({
    reportedQueue: [],
    questionBank: [],
    categories: [],
  });
  const [contentSearch, setContentSearch] = useState('');
  const [contentDifficulty, setContentDifficulty] = useState('ALL');
  const [contentCategory, setContentCategory] = useState('ALL');
  const [editingQuestion, setEditingQuestion] = useState<any | null>(null);
  const [deletingQuestion, setDeletingQuestion] = useState<any | null>(null);

  // Feedback state
  const [feedbackList, setFeedbackList] = useState<any[]>([]);
  const [feedbackStatusFilter, setFeedbackStatusFilter] = useState('ALL');
  const [replyModalFeedback, setReplyModalFeedback] = useState<any | null>(null);
  const [replyText, setReplyText] = useState('');

  // LIVE Events & Tournaments state
  const [eventsData, setEventsData] = useState<{
    liveEvents: any[];
    tournaments: any[];
  }>({ liveEvents: [], tournaments: [] });
  const [viewingParticipantsEvent, setViewingParticipantsEvent] = useState<any | null>(null);

  // Community Feed state
  const [feedData, setFeedData] = useState<{ posts: any[]; reports: any[] }>({
    posts: [],
    reports: [],
  });
  const [banPosterModal, setBanPosterModal] = useState<{
    posterUid: string;
    username: string;
    postId?: number;
  } | null>(null);
  const [banPosterReason, setBanPosterReason] = useState('');

  // Settings state
  const [settingsForm, setSettingsForm] = useState({
    maintenanceMode: false,
    announcementBanner: '',
    signupsEnabled: true,
  });
  const [savingSettings, setSavingSettings] = useState(false);

  // Question Bank & 66-Book Coverage state (Phase 8)
  const [qbOverview, setQbOverview] = useState<any | null>(null);
  const [qbSubTab, setQbSubTab] = useState<'inventory' | 'coverage' | 'analytics' | 'controls'>('inventory');
  const [qbBookFilter, setQbBookFilter] = useState<'ALL' | 'OT' | 'NT' | 'LOW' | 'ZERO'>('ALL');
  const [qbBookSearch, setQbBookSearch] = useState('');
  const [selectedBookDetail, setSelectedBookDetail] = useState<any | null>(null);
  const [inspectingQuestion, setInspectingQuestion] = useState<any | null>(null);
  const [qbBatchForm, setQbBatchForm] = useState({
    book: 'Genesis',
    category: 'Old Testament',
    difficulty: 'Medium' as 'Easy' | 'Medium' | 'Hard',
    count: 5,
  });
  const [qbConfigForm, setQbConfigForm] = useState({
    targetVerifiedQuestions: 5000,
    replenishmentBatchSize: 500,
    minQuestionsPerBook: 10,
    autoReplenishEnabled: true,
  });
  const [qbBusy, setQbBusy] = useState(false);

  // Helper for authenticated admin fetch + Edge Function invocation
  const adminHeaders = useCallback(async () => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'x-admin-uid': adminUid,
      'x-admin-email': adminEmail,
    };
    try {
      const supa = getSupabaseClient();
      if (supa) {
        const { data } = await supa.auth.getSession();
        if (data?.session?.access_token) {
          headers['Authorization'] = `Bearer ${data.session.access_token}`;
        }
      }
    } catch {
      // ignore
    }
    return headers;
  }, [adminUid, adminEmail]);

  const invokeEdgeOrServerAction = useCallback(
    async (endpoint: string, payload: Record<string, any>) => {
      // Try Supabase Edge Function `admin-actions` first if a Supabase session is active
      try {
        const supa = getSupabaseClient();
        if (supa) {
          const { data: sessionData } = await supa.auth.getSession();
          if (sessionData?.session?.access_token) {
            const edgeRes = await supa.functions.invoke('admin-actions', {
              body: payload,
            });
            if (!edgeRes.error && edgeRes.data?.success) {
              // Also sync with backend server so PostgreSQL / in-memory stays in sync
              const headers = await adminHeaders();
              await fetch(endpoint, {
                method: 'POST',
                headers,
                body: JSON.stringify({ ...payload, adminUid, adminEmail }),
              }).catch(() => {});
              return edgeRes.data;
            }
          }
        }
      } catch {
        // Fallback to backend server route
      }

      const headers = await adminHeaders();
      const res = await fetch(endpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify({ ...payload, adminUid, adminEmail }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Admin operation failed');
      }
      return data;
    },
    [adminHeaders, adminUid, adminEmail]
  );

  // Loaders
  const loadOverview = useCallback(async () => {
    setLoading(true);
    try {
      const headers = await adminHeaders();
      const res = await fetch('/api/admin/dashboard', { headers });
      const data = await res.json();
      if (res.ok) {
        setDashboard(data);
        if (data.settings) {
          setSettingsForm({
            maintenanceMode: Boolean(data.settings.maintenanceMode),
            announcementBanner: data.settings.announcementBanner || '',
            signupsEnabled: data.settings.signupsEnabled !== false,
          });
        }
      } else {
        showToast(data.error || 'Failed to load dashboard', 'error');
      }
    } catch (e: any) {
      showToast(e.message || 'Error loading dashboard', 'error');
    } finally {
      setLoading(false);
    }
  }, [adminHeaders, showToast]);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const headers = await adminHeaders();
      const qs = new URLSearchParams({
        search: userSearch,
        country: userCountryFilter,
        status: userStatusFilter,
        dateJoined: userJoinedFilter,
        sortBy: userSortBy,
        sortDir: userSortDir,
        page: String(userPage),
        pageSize: '12',
      });
      const res = await fetch(`/api/admin/users?${qs.toString()}`, { headers });
      const data = await res.json();
      if (res.ok) {
        setUsersData(data);
      } else {
        showToast(data.error || 'Failed to load users', 'error');
      }
    } catch (e: any) {
      showToast(e.message || 'Error loading users', 'error');
    } finally {
      setLoading(false);
    }
  }, [
    adminHeaders,
    userSearch,
    userCountryFilter,
    userStatusFilter,
    userJoinedFilter,
    userSortBy,
    userSortDir,
    userPage,
    showToast,
  ]);

  const loadUserDetail = useCallback(
    async (uid: string) => {
      setSelectedUserUid(uid);
      setLoadingUserDetail(true);
      try {
        const headers = await adminHeaders();
        const res = await fetch(`/api/admin/users/${encodeURIComponent(uid)}`, {
          headers,
        });
        const data = await res.json();
        if (res.ok) {
          setUserDetail(data);
        } else {
          showToast(data.error || 'Failed to load user details', 'error');
        }
      } catch (e: any) {
        showToast(e.message || 'Error loading user details', 'error');
      } finally {
        setLoadingUserDetail(false);
      }
    },
    [adminHeaders, showToast]
  );

  const loadContent = useCallback(async () => {
    setLoading(true);
    try {
      const headers = await adminHeaders();
      const qs = new URLSearchParams({
        search: contentSearch,
        difficulty: contentDifficulty,
        category: contentCategory,
      });
      const res = await fetch(`/api/admin/content?${qs.toString()}`, { headers });
      const data = await res.json();
      if (res.ok) {
        setContentData(data);
      }
    } catch (e: any) {
      showToast(e.message || 'Failed to load content', 'error');
    } finally {
      setLoading(false);
    }
  }, [adminHeaders, contentSearch, contentDifficulty, contentCategory, showToast]);

  const loadFeedback = useCallback(async () => {
    setLoading(true);
    try {
      const headers = await adminHeaders();
      const res = await fetch(
        `/api/admin/feedback?status=${encodeURIComponent(feedbackStatusFilter)}`,
        { headers }
      );
      const data = await res.json();
      if (res.ok) {
        setFeedbackList(data.feedback || []);
      }
    } catch (e: any) {
      showToast(e.message || 'Failed to load feedback', 'error');
    } finally {
      setLoading(false);
    }
  }, [adminHeaders, feedbackStatusFilter, showToast]);

  const loadEvents = useCallback(async () => {
    setLoading(true);
    try {
      const headers = await adminHeaders();
      const res = await fetch('/api/admin/events', { headers });
      const data = await res.json();
      if (res.ok) {
        setEventsData({
          liveEvents: data.liveEvents || [],
          tournaments: data.tournaments || [],
        });
      }
    } catch (e: any) {
      showToast(e.message || 'Failed to load LIVE events', 'error');
    } finally {
      setLoading(false);
    }
  }, [adminHeaders, showToast]);

  const loadFeed = useCallback(async () => {
    setLoading(true);
    try {
      const headers = await adminHeaders();
      const res = await fetch('/api/admin/feed', { headers });
      const data = await res.json();
      if (res.ok) {
        setFeedData({
          posts: data.posts || [],
          reports: data.reports || [],
        });
      }
    } catch (e: any) {
      showToast(e.message || 'Failed to load community feed', 'error');
    } finally {
      setLoading(false);
    }
  }, [adminHeaders, showToast]);

  const loadQuestionBank = useCallback(async () => {
    setLoading(true);
    try {
      const headers = await adminHeaders();
      const res = await fetch('/api/admin/question-bank/overview', { headers });
      const data = await res.json();
      if (res.ok) {
        setQbOverview(data);
        if (data.config) {
          setQbConfigForm({
            targetVerifiedQuestions: Number(data.config.targetVerifiedQuestions) || 5000,
            replenishmentBatchSize: Number(data.config.replenishmentBatchSize) || 500,
            minQuestionsPerBook: Number(data.config.minQuestionsPerBook) || 10,
            autoReplenishEnabled: data.config.autoReplenishEnabled !== false,
          });
        }
      } else {
        showToast(data.error || 'Failed to load Question Bank overview', 'error');
      }
    } catch (e: any) {
      showToast(e.message || 'Failed to load Question Bank overview', 'error');
    } finally {
      setLoading(false);
    }
  }, [adminHeaders, showToast]);

  const handleSaveQbConfig = async (e: React.FormEvent) => {
    e.preventDefault();
    setQbBusy(true);
    try {
      const headers = await adminHeaders();
      const res = await fetch('/api/admin/question-bank/config', {
        method: 'POST',
        headers,
        body: JSON.stringify({ ...qbConfigForm, adminUid, adminEmail }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to save target inventory settings');
      showToast('Question Bank target settings saved', 'success');
      loadQuestionBank();
    } catch (err: any) {
      showToast(err.message || 'Failed to save settings', 'error');
    } finally {
      setQbBusy(false);
    }
  };

  const handleGenerateQbBatch = async (overrideBook?: string) => {
    setQbBusy(true);
    try {
      const headers = await adminHeaders();
      const payload = {
        book: overrideBook || qbBatchForm.book,
        category: qbBatchForm.category,
        difficulty: qbBatchForm.difficulty,
        count: qbBatchForm.count,
        adminUid,
        adminEmail,
      };
      const res = await fetch('/api/admin/question-bank/generate-batch', {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Batch generation failed');
      showToast(
        `Batch complete (${data.targetBook}): +${data.verifiedCount} verified, ${data.pendingCount} pending, ${data.rejectedCount} rejected`,
        'success'
      );
      loadQuestionBank();
    } catch (err: any) {
      showToast(err.message || 'Batch generation failed', 'error');
    } finally {
      setQbBusy(false);
    }
  };

  const handleTriggerReplenishment = async () => {
    setQbBusy(true);
    try {
      const headers = await adminHeaders();
      const res = await fetch('/api/admin/question-bank/replenish', {
        method: 'POST',
        headers,
        body: JSON.stringify({ maxQuestionsThisRun: 12, adminUid, adminEmail }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Automated replenishment failed');
      showToast(
        `Replenishment completed: +${data.generatedVerified} verified across ${(data.booksProcessed || []).join(', ') || 'canonical books'}`,
        'success'
      );
      loadQuestionBank();
    } catch (err: any) {
      showToast(err.message || 'Replenishment failed', 'error');
    } finally {
      setQbBusy(false);
    }
  };

  const handleReviewPendingQuestion = async (questionId: number, decision: 'approve' | 'reject') => {
    setQbBusy(true);
    try {
      const headers = await adminHeaders();
      const res = await fetch('/api/admin/question-bank/review', {
        method: 'POST',
        headers,
        body: JSON.stringify({ questionId, decision, adminUid, adminEmail }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Review failed');
      showToast(data.message || `Question #${questionId} ${decision}d`, 'success');
      loadQuestionBank();
    } catch (err: any) {
      showToast(err.message || 'Review action failed', 'error');
    } finally {
      setQbBusy(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'overview' || activeTab === 'settings') loadOverview();
    if (activeTab === 'question-bank') loadQuestionBank();
    if (activeTab === 'users') loadUsers();
    if (activeTab === 'content') loadContent();
    if (activeTab === 'feedback') loadFeedback();
    if (activeTab === 'events') loadEvents();
    if (activeTab === 'feed') loadFeed();
  }, [
    activeTab,
    loadOverview,
    loadQuestionBank,
    loadUsers,
    loadContent,
    loadFeedback,
    loadEvents,
    loadFeed,
  ]);

  // Execute User Actions
  const handleConfirmUserAction = async () => {
    if (!actionModal.targetUser || !actionModal.type) return;
    const targetUid = actionModal.targetUser.uid;
    setSubmittingAction(true);

    try {
      if (actionModal.type === 'delete_user') {
        const expected = actionModal.targetUser.username || 'DELETE';
        if (
          deleteConfirmText.trim() !== expected &&
          deleteConfirmText.trim() !== 'DELETE'
        ) {
          showToast(`Please type "${expected}" to confirm deletion.`, 'error');
          setSubmittingAction(false);
          return;
        }
      }

      const payload: Record<string, any> = {
        targetUid,
        action: actionModal.type,
        reason: actionReason.trim() || undefined,
        suspendDays: actionModal.type === 'suspend' ? suspendDays : undefined,
        newRole: actionModal.type === 'role' ? targetRole : undefined,
      };

      const res = await invokeEdgeOrServerAction('/api/admin/users/action', payload);
      showToast(res.message || 'User action completed successfully', 'success');
      setActionModal({ type: null, targetUser: null });
      setActionReason('');
      setDeleteConfirmText('');

      if (actionModal.type === 'delete_user' && selectedUserUid === targetUid) {
        setSelectedUserUid(null);
        setUserDetail(null);
      } else if (selectedUserUid === targetUid) {
        loadUserDetail(targetUid);
      }
      loadUsers();
    } catch (err: any) {
      showToast(err.message || 'Action failed', 'error');
    } finally {
      setSubmittingAction(false);
    }
  };

  // Execute Content Actions
  const handleQuestionAction = async (
    action: 'edit' | 'delete' | 'dismiss_report' | 'regenerate_gemini',
    questionId?: number,
    reportId?: number,
    updates?: any
  ) => {
    setSubmittingAction(true);
    try {
      const headers = await adminHeaders();
      const res = await fetch('/api/admin/content/action', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          adminUid,
          adminEmail,
          action,
          questionId,
          reportId,
          updates,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Content action failed');
      showToast(data.message || 'Content updated', 'success');
      setEditingQuestion(null);
      setDeletingQuestion(null);
      loadContent();
    } catch (e: any) {
      showToast(e.message || 'Content action failed', 'error');
    } finally {
      setSubmittingAction(false);
    }
  };

  // Execute Feedback Actions
  const handleFeedbackAction = async (
    feedbackId: number,
    action: 'mark_status' | 'reply_email',
    status?: 'new' | 'reviewed' | 'resolved',
    replyMessage?: string,
    recipientEmail?: string
  ) => {
    setSubmittingAction(true);
    try {
      const headers = await adminHeaders();
      const res = await fetch('/api/admin/feedback/action', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          adminUid,
          adminEmail,
          feedbackId,
          action,
          status,
          replyText: replyMessage,
          recipientEmail,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Feedback action failed');
      showToast(data.message || 'Feedback updated', 'success');
      setReplyModalFeedback(null);
      setReplyText('');
      loadFeedback();
    } catch (e: any) {
      showToast(e.message || 'Feedback action failed', 'error');
    } finally {
      setSubmittingAction(false);
    }
  };

  // Execute Event Actions
  const handleEventAction = async (
    action:
      | 'close_live_event'
      | 'remove_live_participant'
      | 'cancel_tournament'
      | 'remove_tournament_participant',
    params: { eventId?: number; roomPin?: string; participantUid?: string }
  ) => {
    setSubmittingAction(true);
    try {
      const headers = await adminHeaders();
      const res = await fetch('/api/admin/events/action', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          adminUid,
          adminEmail,
          action,
          ...params,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Event action failed');
      showToast(data.message || 'Event updated', 'success');
      loadEvents();
    } catch (e: any) {
      showToast(e.message || 'Event action failed', 'error');
    } finally {
      setSubmittingAction(false);
    }
  };

  // Execute Feed Actions
  const handleFeedAction = async (
    action: 'delete_post' | 'ban_poster',
    postId?: number,
    posterUid?: string,
    reason?: string
  ) => {
    setSubmittingAction(true);
    try {
      const headers = await adminHeaders();
      const res = await fetch('/api/admin/feed/action', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          adminUid,
          adminEmail,
          action,
          postId,
          posterUid,
          reason,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Feed action failed');
      showToast(data.message || 'Feed moderation applied', 'success');
      setBanPosterModal(null);
      setBanPosterReason('');
      loadFeed();
    } catch (e: any) {
      showToast(e.message || 'Feed action failed', 'error');
    } finally {
      setSubmittingAction(false);
    }
  };

  // Save Platform Settings
  const handleSaveSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingSettings(true);
    try {
      const res = await invokeEdgeOrServerAction('/api/admin/settings', {
        action: 'update_settings',
        ...settingsForm,
      });
      showToast('Platform settings saved', 'success');
      if (onPlatformSettingsChange && res.settings) {
        onPlatformSettingsChange(res.settings);
      } else if (onPlatformSettingsChange) {
        onPlatformSettingsChange(settingsForm);
      }
    } catch (err: any) {
      showToast(err.message || 'Failed to save settings', 'error');
    } finally {
      setSavingSettings(false);
    }
  };

  const toggleUserSort = (field: string) => {
    if (userSortBy === field) {
      setUserSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setUserSortBy(field);
      setUserSortDir('desc');
    }
  };

  const navItems: { id: AdminTab; label: string; icon: React.ReactNode; badge?: number }[] = [
    {
      id: 'overview',
      label: 'Overview',
      icon: <LayoutDashboard className="w-4 h-4" />,
    },
    {
      id: 'question-bank',
      label: 'Question Bank (66 Books)',
      icon: <Sparkles className="w-4 h-4" />,
      badge: qbOverview?.overallInventory?.verifiedQuestions || dashboard?.cards?.totalQuestions,
    },
    {
      id: 'users',
      label: 'Users',
      icon: <Users className="w-4 h-4" />,
      badge: dashboard?.cards?.totalUsers,
    },
    {
      id: 'content',
      label: 'Content & Reports',
      icon: <BookOpen className="w-4 h-4" />,
      badge: dashboard?.cards?.pendingQuestionReports || undefined,
    },
    {
      id: 'feedback',
      label: 'Feedback',
      icon: <MessageSquare className="w-4 h-4" />,
      badge: dashboard?.cards?.pendingFeedback || undefined,
    },
    {
      id: 'events',
      label: 'LIVE & Tournaments',
      icon: <Radio className="w-4 h-4" />,
      badge: dashboard?.cards?.liveEventsActive || undefined,
    },
    {
      id: 'feed',
      label: 'Community Feed',
      icon: <MessageCircle className="w-4 h-4" />,
    },
    {
      id: 'settings',
      label: 'Settings',
      icon: <Settings className="w-4 h-4" />,
    },
  ];

  // Simple SVG Line Chart renderer (Orange / Emerald accent, dark neutral grid)
  const renderLineChart = (
    title: string,
    subtitle: string,
    series: { date: string; count: number }[],
    strokeColor: string
  ) => {
    const safeSeries = series && series.length > 0 ? series : [{ date: '2026-11-03', count: 0 }];
    const maxVal = Math.max(5, ...safeSeries.map((d) => d.count));
    const width = 560;
    const height = 170;
    const padX = 28;
    const padY = 20;
    const innerW = width - padX * 2;
    const innerH = height - padY * 2;

    const points = safeSeries.map((pt, idx) => {
      const x =
        padX +
        (safeSeries.length > 1 ? (idx / (safeSeries.length - 1)) * innerW : innerW / 2);
      const y = padY + innerH - (pt.count / maxVal) * innerH;
      return { x, y, ...pt };
    });

    const polylinePoints = points.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
    const areaPoints = `${points[0].x.toFixed(1)},${height - padY} ${polylinePoints} ${points[
      points.length - 1
    ].x.toFixed(1)},${height - padY}`;
    const total30d = safeSeries.reduce((acc, cur) => acc + cur.count, 0);

    return (
      <div className="bg-[#141414] border border-[#262626] rounded-2xl p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-sm font-bold text-[#F5F5F5]">{title}</h3>
            <p className="text-xs text-[#A3A3A3]">{subtitle}</p>
          </div>
          <div className="text-right">
            <span className="text-lg font-extrabold text-[#F5F5F5]">{total30d}</span>
            <span className="block text-[10px] uppercase tracking-wider text-[#A3A3A3]">
              30-Day Total
            </span>
          </div>
        </div>
        <div className="w-full overflow-hidden">
          <svg
            viewBox={`0 0 ${width} ${height}`}
            className="w-full h-44 overflow-visible"
          >
            {/* Horizontal reference lines */}
            {[0, 0.5, 1].map((ratio, i) => {
              const y = padY + innerH * (1 - ratio);
              const val = Math.round(maxVal * ratio);
              return (
                <g key={i}>
                  <line
                    x1={padX}
                    y1={y}
                    x2={width - padX}
                    y2={y}
                    stroke="#262626"
                    strokeDasharray="3 3"
                    strokeWidth="1"
                  />
                  <text
                    x={4}
                    y={y + 3}
                    fill="#737373"
                    fontSize="9"
                    fontFamily="monospace"
                  >
                    {val}
                  </text>
                </g>
              );
            })}
            {/* Area under curve */}
            <polygon points={areaPoints} fill={strokeColor} fillOpacity="0.12" />
            {/* Line */}
            <polyline
              fill="none"
              stroke={strokeColor}
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              points={polylinePoints}
            />
            {/* Points */}
            {points.map((pt, i) => (
              <g key={i} className="group">
                <circle
                  cx={pt.x}
                  cy={pt.y}
                  r={pt.count > 0 ? 3 : 2}
                  fill={pt.count > 0 ? strokeColor : '#262626'}
                />
                <title>{`${pt.date}: ${pt.count}`}</title>
              </g>
            ))}
          </svg>
          <div className="flex justify-between text-[10px] text-[#737373] font-mono mt-1 px-2">
            <span>{safeSeries[0]?.date}</span>
            <span>{safeSeries[Math.floor(safeSeries.length / 2)]?.date}</span>
            <span>{safeSeries[safeSeries.length - 1]?.date}</span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-[82vh] bg-[#0A0A0A] text-[#F5F5F5] flex flex-col lg:flex-row rounded-2xl border border-[#262626] overflow-hidden">
      {/* Left Sidebar Navigation */}
      <aside className="w-full lg:w-64 bg-[#141414] border-b lg:border-b-0 lg:border-r border-[#262626] flex flex-col justify-between shrink-0">
        <div>
          <div className="p-5 border-b border-[#262626] flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img
                src="/quickibible-icon.png"
                alt="QuickiBible Admin"
                className="w-9 h-9 rounded-xl object-contain shadow-md border border-amber-500/30 shrink-0"
              />
              <div>
                <h2 className="flex items-center gap-1.5 text-sm font-extrabold tracking-tight text-[#F5F5F5]">
                  Quick!Bible Admin
                  <span className="text-[#F59E0B] text-[7px] font-light font-serif tracking-widest uppercase translate-y-[0.5px]">BETA</span>
                </h2>
                <p className="text-[11px] text-[#A3A3A3] truncate max-w-[140px]">
                  {adminEmail}
                </p>
              </div>
            </div>
          </div>

          <nav className="p-3 flex lg:flex-col gap-1.5 overflow-x-auto">
            {navItems.map((item) => {
              const isActive = activeTab === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id)}
                  className={`flex items-center justify-between gap-2.5 px-3.5 py-2.5 rounded-xl text-xs font-semibold transition-all whitespace-nowrap ${
                    isActive
                      ? 'bg-[#F97316] text-[#0A0A0A] font-bold shadow-sm'
                      : 'text-[#A3A3A3] hover:text-[#F5F5F5] hover:bg-[#1C1C1C]'
                  }`}
                >
                  <span className="flex items-center gap-2.5">
                    {item.icon}
                    <span>{item.label}</span>
                  </span>
                  {item.badge !== undefined && item.badge > 0 && (
                    <span
                      className={`px-1.5 py-0.5 text-[10px] rounded-md font-mono font-bold ${
                        isActive
                          ? 'bg-[#0A0A0A]/20 text-[#0A0A0A]'
                          : 'bg-[#262626] text-[#F97316]'
                      }`}
                    >
                      {item.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-4 border-t border-[#262626] hidden lg:block">
          <button
            onClick={onExitAdmin}
            className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl bg-[#1C1C1C] hover:bg-[#262626] text-xs font-semibold text-[#D4D4D4] transition-colors border border-[#262626]"
          >
            <span className="flex items-center gap-1.5">
              <ArrowLeft className="w-4 h-4" />
              Return to Quick!Bible
              <span className="text-[#F59E0B] text-[7px] font-light font-serif tracking-widest uppercase">BETA</span>
            </span>
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0 bg-[#0A0A0A]">
        {/* Top Header Bar */}
        <header className="px-6 py-4 bg-[#141414] border-b border-[#262626] flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-lg font-extrabold text-[#F5F5F5] capitalize">
              {navItems.find((n) => n.id === activeTab)?.label || 'Admin Panel'}
            </h1>
            <p className="text-xs text-[#A3A3A3]">
              Server-verified administrator controls • All actions logged to audit trail
            </p>
          </div>
          <div className="flex items-center gap-2.5">
            <button
              onClick={() => {
                if (activeTab === 'overview') loadOverview();
                if (activeTab === 'users') loadUsers();
                if (activeTab === 'content') loadContent();
                if (activeTab === 'feedback') loadFeedback();
                if (activeTab === 'events') loadEvents();
                if (activeTab === 'feed') loadFeed();
                if (activeTab === 'settings') loadOverview();
              }}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-[#1C1C1C] hover:bg-[#262626] border border-[#262626] text-xs font-semibold text-[#E5E5E5] transition-colors"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin text-[#F97316]' : ''}`} />
              Refresh
            </button>
            <button
              onClick={onExitAdmin}
              className="lg:hidden flex items-center gap-1.5 px-3 py-2 rounded-xl bg-[#1C1C1C] border border-[#262626] text-xs font-semibold text-[#D4D4D4]"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Back
            </button>
          </div>
        </header>

        {/* Body Content */}
        <div className="p-6 flex-1 space-y-6 overflow-x-hidden">
          {/* ==================== 1. OVERVIEW TAB ==================== */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Stat Cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="bg-[#141414] border border-[#262626] rounded-2xl p-4">
                  <div className="flex items-center justify-between text-[#A3A3A3] text-xs mb-1">
                    <span>Total Users</span>
                    <Users className="w-4 h-4 text-[#F97316]" />
                  </div>
                  <div className="text-2xl font-extrabold text-[#F5F5F5]">
                    {dashboard?.cards?.totalUsers ?? '—'}
                  </div>
                  <div className="text-[11px] text-[#A3A3A3] mt-1">
                    +{dashboard?.cards?.newToday ?? 0} today • +{dashboard?.cards?.new7d ?? 0} (7d) • +
                    {dashboard?.cards?.new30d ?? 0} (30d)
                  </div>
                </div>

                <div className="bg-[#141414] border border-[#262626] rounded-2xl p-4">
                  <div className="flex items-center justify-between text-[#A3A3A3] text-xs mb-1">
                    <span>Active Scholars</span>
                    <Activity className="w-4 h-4 text-[#10B981]" />
                  </div>
                  <div className="text-2xl font-extrabold text-[#F5F5F5]">
                    {dashboard?.cards?.active24h ?? '—'}{' '}
                    <span className="text-xs font-normal text-[#A3A3A3]">24h</span>
                  </div>
                  <div className="text-[11px] text-[#10B981] mt-1">
                    {dashboard?.cards?.active7d ?? 0} active in last 7 days
                  </div>
                </div>

                <div className="bg-[#141414] border border-[#262626] rounded-2xl p-4">
                  <div className="flex items-center justify-between text-[#A3A3A3] text-xs mb-1">
                    <span>Games Played Today</span>
                    <Trophy className="w-4 h-4 text-[#F59E0B]" />
                  </div>
                  <div className="text-2xl font-extrabold text-[#F5F5F5]">
                    {dashboard?.cards?.gamesPlayedToday ?? '—'}
                  </div>
                  <div className="text-[11px] text-[#F59E0B] mt-1">
                    {dashboard?.cards?.liveEventsActive ?? 0} LIVE events active now
                  </div>
                </div>

                <div className="bg-[#141414] border border-[#262626] rounded-2xl p-4">
                  <div className="flex items-center justify-between text-[#A3A3A3] text-xs mb-1">
                    <span>Pending Queues</span>
                    <Flag className="w-4 h-4 text-[#F97316]" />
                  </div>
                  <div className="flex items-baseline gap-4">
                    <div>
                      <span className="text-2xl font-extrabold text-[#F97316]">
                        {dashboard?.cards?.pendingFeedback ?? 0}
                      </span>
                      <span className="text-[11px] text-[#A3A3A3] block">Feedback</span>
                    </div>
                    <div>
                      <span className="text-2xl font-extrabold text-[#F59E0B]">
                        {dashboard?.cards?.pendingQuestionReports ?? 0}
                      </span>
                      <span className="text-[11px] text-[#A3A3A3] block">Q. Reports</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* 30-Day Line Charts */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                {renderLineChart(
                  'Signups Per Day (Last 30 Days)',
                  'Daily new scholar registrations',
                  dashboard?.charts?.signupsPerDay || [],
                  '#F97316'
                )}
                {renderLineChart(
                  'Games Played Per Day (Last 30 Days)',
                  'Completed Solo, Timed, MP & LIVE matches',
                  dashboard?.charts?.gamesPerDay || [],
                  '#10B981'
                )}
              </div>

              {/* Recent Admin Audit Log */}
              <div className="bg-[#141414] border border-[#262626] rounded-2xl overflow-hidden">
                <div className="px-5 py-4 border-b border-[#262626] flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-bold text-[#F5F5F5]">
                      Recent Admin Audit Trail
                    </h3>
                    <p className="text-xs text-[#A3A3A3]">
                      Immutable log of administrative actions (`admin_audit_log`)
                    </p>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-[#1C1C1C] text-[#A3A3A3] uppercase tracking-wider text-[10px]">
                      <tr>
                        <th className="py-3 px-4">Timestamp</th>
                        <th className="py-3 px-4">Admin</th>
                        <th className="py-3 px-4">Action</th>
                        <th className="py-3 px-4">Target User</th>
                        <th className="py-3 px-4">Details</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#262626]">
                      {(dashboard?.auditLogs || []).length === 0 ? (
                        <tr>
                          <td colSpan={5} className="py-8 text-center text-[#737373]">
                            No admin actions recorded yet.
                          </td>
                        </tr>
                      ) : (
                        (dashboard?.auditLogs || []).slice(0, 12).map((log: any) => (
                          <tr key={log.id} className="hover:bg-[#1C1C1C]/60">
                            <td className="py-2.5 px-4 font-mono text-[#A3A3A3]">
                              {new Date(log.createdAt).toLocaleString()}
                            </td>
                            <td className="py-2.5 px-4 font-mono text-[#E5E5E5]">
                              {String(log.adminId).slice(0, 16)}
                            </td>
                            <td className="py-2.5 px-4">
                              <span className="px-2 py-0.5 rounded-md bg-[#262626] text-[#F97316] font-mono font-semibold">
                                {log.action}
                              </span>
                            </td>
                            <td className="py-2.5 px-4 font-mono text-[#A3A3A3]">
                              {log.targetUserId || '—'}
                            </td>
                            <td className="py-2.5 px-4 text-[#A3A3A3] font-mono truncate max-w-xs">
                              {JSON.stringify(log.details || {})}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* ==================== 2. USERS TAB ==================== */}
          {activeTab === 'users' && (
            <div className="space-y-4">
              {/* Search & Filters */}
              <div className="bg-[#141414] border border-[#262626] rounded-2xl p-4 flex flex-wrap items-center gap-3">
                <div className="relative flex-1 min-w-[220px]">
                  <Search className="w-4 h-4 text-[#737373] absolute left-3.5 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    value={userSearch}
                    onChange={(e) => {
                      setUserSearch(e.target.value);
                      setUserPage(1);
                    }}
                    placeholder="Search username, email, or UID..."
                    className="w-full pl-9 pr-4 py-2 rounded-xl bg-[#0A0A0A] border border-[#262626] text-xs text-[#F5F5F5] placeholder-[#737373] focus:outline-none focus:border-[#F97316]"
                  />
                </div>

                <select
                  value={userStatusFilter}
                  onChange={(e) => {
                    setUserStatusFilter(e.target.value);
                    setUserPage(1);
                  }}
                  className="px-3 py-2 rounded-xl bg-[#0A0A0A] border border-[#262626] text-xs text-[#E5E5E5] focus:outline-none focus:border-[#F97316]"
                >
                  <option value="ALL">All Statuses</option>
                  <option value="active">Active</option>
                  <option value="banned">Banned</option>
                  <option value="suspended">Suspended</option>
                </select>

                <select
                  value={userCountryFilter}
                  onChange={(e) => {
                    setUserCountryFilter(e.target.value);
                    setUserPage(1);
                  }}
                  className="px-3 py-2 rounded-xl bg-[#0A0A0A] border border-[#262626] text-xs text-[#E5E5E5] focus:outline-none focus:border-[#F97316]"
                >
                  <option value="ALL">All Countries</option>
                  {(usersData.countries || []).map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>

                <select
                  value={userJoinedFilter}
                  onChange={(e) => {
                    setUserJoinedFilter(e.target.value);
                    setUserPage(1);
                  }}
                  className="px-3 py-2 rounded-xl bg-[#0A0A0A] border border-[#262626] text-xs text-[#E5E5E5] focus:outline-none focus:border-[#F97316]"
                >
                  <option value="ALL">Joined: Any Time</option>
                  <option value="today">Joined Today</option>
                  <option value="7d">Last 7 Days</option>
                  <option value="30d">Last 30 Days</option>
                </select>
              </div>

              {/* Users Table */}
              <div className="bg-[#141414] border border-[#262626] rounded-2xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-[#1C1C1C] text-[#A3A3A3] uppercase tracking-wider text-[10px]">
                      <tr>
                        <th className="py-3.5 px-4">Scholar</th>
                        <th className="py-3.5 px-3">Country</th>
                        <th
                          onClick={() => toggleUserSort('joinedAt')}
                          className="py-3.5 px-3 cursor-pointer hover:text-[#F5F5F5]"
                        >
                          <span className="inline-flex items-center gap-1">
                            Joined <ArrowUpDown className="w-3 h-3" />
                          </span>
                        </th>
                        <th
                          onClick={() => toggleUserSort('lastActiveAt')}
                          className="py-3.5 px-3 cursor-pointer hover:text-[#F5F5F5]"
                        >
                          <span className="inline-flex items-center gap-1">
                            Last Active <ArrowUpDown className="w-3 h-3" />
                          </span>
                        </th>
                        <th
                          onClick={() => toggleUserSort('rating')}
                          className="py-3.5 px-3 cursor-pointer hover:text-[#F5F5F5]"
                        >
                          <span className="inline-flex items-center gap-1">
                            Rating <ArrowUpDown className="w-3 h-3" />
                          </span>
                        </th>
                        <th
                          onClick={() => toggleUserSort('gamesPlayed')}
                          className="py-3.5 px-3 cursor-pointer hover:text-[#F5F5F5]"
                        >
                          <span className="inline-flex items-center gap-1">
                            Games <ArrowUpDown className="w-3 h-3" />
                          </span>
                        </th>
                        <th className="py-3.5 px-3">Role / Status</th>
                        <th className="py-3.5 px-4 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#262626]">
                      {usersData.users.length === 0 ? (
                        <tr>
                          <td colSpan={8} className="py-10 text-center text-[#737373]">
                            No users match the current filters.
                          </td>
                        </tr>
                      ) : (
                        usersData.users.map((u) => (
                          <tr
                            key={u.uid}
                            className="hover:bg-[#1C1C1C]/70 transition-colors"
                          >
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-3">
                                {u.avatarUrl ? (
                                  <img
                                    src={u.avatarUrl}
                                    alt={u.username}
                                    className="w-8 h-8 rounded-full object-cover border border-[#262626]"
                                  />
                                ) : (
                                  <div className="w-8 h-8 rounded-full bg-[#262626] text-[#F97316] flex items-center justify-center font-bold text-xs">
                                    {(u.username || 'S').slice(0, 2).toUpperCase()}
                                  </div>
                                )}
                                <div>
                                  <div className="font-bold text-[#F5F5F5]">
                                    {u.username}
                                  </div>
                                  <div className="text-[11px] text-[#A3A3A3]">
                                    {u.email}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="py-3 px-3 font-mono text-[#D4D4D4]">
                              {u.country || 'PH'}
                            </td>
                            <td className="py-3 px-3 text-[#A3A3A3]">
                              {u.joinedAt
                                ? new Date(u.joinedAt).toLocaleDateString()
                                : '—'}
                            </td>
                            <td className="py-3 px-3 text-[#A3A3A3]">
                              {u.lastActiveAt
                                ? new Date(u.lastActiveAt).toLocaleDateString()
                                : '—'}
                            </td>
                            <td className="py-3 px-3 font-mono font-bold text-[#F59E0B]">
                              {u.rating ?? 1000}
                            </td>
                            <td className="py-3 px-3 font-mono text-[#E5E5E5]">
                              {u.gamesPlayed ?? 0}
                            </td>
                            <td className="py-3 px-3">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                {u.role === 'admin' && (
                                  <span className="px-2 py-0.5 rounded-md bg-[#F97316]/20 border border-[#F97316]/40 text-[#F97316] text-[10px] font-bold uppercase">
                                    Admin
                                  </span>
                                )}
                                <span
                                  className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase ${
                                    u.status === 'banned'
                                      ? 'bg-red-950/60 border border-red-800/50 text-red-400'
                                      : u.status === 'suspended'
                                      ? 'bg-[#F59E0B]/20 border border-[#F59E0B]/40 text-[#F59E0B]'
                                      : 'bg-[#10B981]/15 border border-[#10B981]/30 text-[#10B981]'
                                  }`}
                                >
                                  {u.status || 'active'}
                                </span>
                              </div>
                            </td>
                            <td className="py-3 px-4 text-right">
                              <button
                                onClick={() => loadUserDetail(u.uid)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#1C1C1C] hover:bg-[#262626] border border-[#262626] text-xs font-semibold text-[#F97316]"
                              >
                                <Eye className="w-3.5 h-3.5" />
                                Inspect
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="px-4 py-3 border-t border-[#262626] flex items-center justify-between text-xs text-[#A3A3A3]">
                  <span>
                    Showing page {usersData.page} of {usersData.totalPages} ({usersData.total}{' '}
                    total users)
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      disabled={userPage <= 1}
                      onClick={() => setUserPage((p) => Math.max(1, p - 1))}
                      className="p-1.5 rounded-lg bg-[#1C1C1C] border border-[#262626] disabled:opacity-40"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button
                      disabled={userPage >= usersData.totalPages}
                      onClick={() =>
                        setUserPage((p) => Math.min(usersData.totalPages, p + 1))
                      }
                      className="p-1.5 rounded-lg bg-[#1C1C1C] border border-[#262626] disabled:opacity-40"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ==================== 3. CONTENT TAB ==================== */}
          {activeTab === 'content' && (
            <div className="space-y-5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2 bg-[#141414] p-1 rounded-xl border border-[#262626]">
                  <button
                    onClick={() => setContentSubTab('reported')}
                    className={`px-3.5 py-2 rounded-lg text-xs font-bold transition-all ${
                      contentSubTab === 'reported'
                        ? 'bg-[#F97316] text-[#0A0A0A]'
                        : 'text-[#A3A3A3] hover:text-[#F5F5F5]'
                    }`}
                  >
                    Reported Questions Queue ({contentData.reportedQueue.length})
                  </button>
                  <button
                    onClick={() => setContentSubTab('bank')}
                    className={`px-3.5 py-2 rounded-lg text-xs font-bold transition-all ${
                      contentSubTab === 'bank'
                        ? 'bg-[#F97316] text-[#0A0A0A]'
                        : 'text-[#A3A3A3] hover:text-[#F5F5F5]'
                    }`}
                  >
                    Question Bank Browser ({contentData.questionBank.length})
                  </button>
                </div>

                {contentSubTab === 'bank' && (
                  <div className="flex flex-wrap items-center gap-2">
                    <input
                      type="text"
                      value={contentSearch}
                      onChange={(e) => setContentSearch(e.target.value)}
                      placeholder="Search question, verse, answer..."
                      className="px-3 py-2 rounded-xl bg-[#141414] border border-[#262626] text-xs text-[#F5F5F5]"
                    />
                    <select
                      value={contentDifficulty}
                      onChange={(e) => setContentDifficulty(e.target.value)}
                      className="px-3 py-2 rounded-xl bg-[#141414] border border-[#262626] text-xs text-[#E5E5E5]"
                    >
                      <option value="ALL">All Difficulties</option>
                      <option value="Easy">Easy</option>
                      <option value="Intermediate">Intermediate</option>
                      <option value="Advanced">Advanced</option>
                      <option value="Scholar">Scholar</option>
                    </select>
                    <select
                      value={contentCategory}
                      onChange={(e) => setContentCategory(e.target.value)}
                      className="px-3 py-2 rounded-xl bg-[#141414] border border-[#262626] text-xs text-[#E5E5E5]"
                    >
                      <option value="ALL">All Categories</option>
                      {(contentData.categories || []).map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {contentSubTab === 'reported' ? (
                <div className="space-y-3">
                  {contentData.reportedQueue.length === 0 ? (
                    <div className="bg-[#141414] border border-[#262626] rounded-2xl p-10 text-center text-xs text-[#737373]">
                      No reported questions in the queue. All scripture questions are clean!
                    </div>
                  ) : (
                    contentData.reportedQueue.map((item) => (
                      <div
                        key={`${item.reportId}-${item.questionId}`}
                        className="bg-[#141414] border border-[#262626] rounded-2xl p-5 space-y-3"
                      >
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <span className="px-2 py-0.5 rounded bg-[#F59E0B]/20 text-[#F59E0B] text-[10px] font-bold uppercase">
                              Reported: {item.reason}
                            </span>
                            <span className="text-xs text-[#A3A3A3]">
                              by <strong className="text-[#E5E5E5]">{item.reporterUsername}</strong>
                            </span>
                            <span className="text-xs font-mono text-[#F97316]">
                              {item.question?.scriptureReference}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => setEditingQuestion(item.question)}
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-[#1C1C1C] hover:bg-[#262626] border border-[#262626] text-xs text-[#E5E5E5]"
                            >
                              <Edit3 className="w-3.5 h-3.5 text-[#F97316]" /> Edit
                            </button>
                            <button
                              disabled={submittingAction}
                              onClick={() =>
                                handleQuestionAction(
                                  'regenerate_gemini',
                                  item.questionId,
                                  item.reportId
                                )
                              }
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-[#1C1C1C] hover:bg-[#262626] border border-[#262626] text-xs text-[#10B981]"
                            >
                              <Sparkles className="w-3.5 h-3.5" /> Regenerate with Gemini
                            </button>
                            <button
                              disabled={submittingAction}
                              onClick={() =>
                                handleQuestionAction(
                                  'dismiss_report',
                                  item.questionId,
                                  item.reportId
                                )
                              }
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-[#1C1C1C] hover:bg-[#262626] border border-[#262626] text-xs text-[#A3A3A3]"
                            >
                              <CheckCircle2 className="w-3.5 h-3.5" /> Dismiss Report
                            </button>
                            <button
                              onClick={() => setDeletingQuestion(item.question)}
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-red-950/40 hover:bg-red-950/70 border border-red-900/50 text-xs text-red-400"
                            >
                              <Trash2 className="w-3.5 h-3.5" /> Delete
                            </button>
                          </div>
                        </div>
                        <p className="text-sm font-bold text-[#F5F5F5]">
                          {item.question?.questionText}
                        </p>
                        <div className="text-xs text-[#A3A3A3] flex flex-wrap gap-4">
                          <span>
                            Correct Answer:{' '}
                            <strong className="text-[#10B981]">
                              {item.question?.correctAnswer}
                            </strong>
                          </span>
                          <span>
                            Options:{' '}
                            {Array.isArray(item.question?.options)
                              ? item.question.options.join(' • ')
                              : '—'}
                          </span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              ) : (
                <div className="bg-[#141414] border border-[#262626] rounded-2xl overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-xs">
                      <thead className="bg-[#1C1C1C] text-[#A3A3A3] uppercase text-[10px]">
                        <tr>
                          <th className="py-3 px-4">ID / Ref</th>
                          <th className="py-3 px-4">Question</th>
                          <th className="py-3 px-3">Correct Answer</th>
                          <th className="py-3 px-3">Category / Diff</th>
                          <th className="py-3 px-4 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#262626]">
                        {contentData.questionBank.map((q) => (
                          <tr key={q.id} className="hover:bg-[#1C1C1C]/60">
                            <td className="py-3 px-4 font-mono">
                              <div className="text-[#F97316] font-bold">
                                {q.scriptureReference}
                              </div>
                              <div className="text-[10px] text-[#737373]">#{q.id}</div>
                            </td>
                            <td className="py-3 px-4 text-[#F5F5F5] max-w-md">
                              {q.questionText}
                            </td>
                            <td className="py-3 px-3 text-[#10B981] font-semibold">
                              {q.correctAnswer}
                            </td>
                            <td className="py-3 px-3">
                              <div className="text-[#E5E5E5]">{q.category}</div>
                              <div className="text-[10px] text-[#F59E0B]">
                                {q.difficulty}
                              </div>
                            </td>
                            <td className="py-3 px-4 text-right space-x-1.5 whitespace-nowrap">
                              <button
                                onClick={() => setEditingQuestion(q)}
                                className="p-1.5 rounded-lg bg-[#1C1C1C] hover:bg-[#262626] border border-[#262626] text-[#E5E5E5]"
                                title="Edit Question"
                              >
                                <Edit3 className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() =>
                                  handleQuestionAction('regenerate_gemini', q.id)
                                }
                                className="p-1.5 rounded-lg bg-[#1C1C1C] hover:bg-[#262626] border border-[#262626] text-[#10B981]"
                                title="Regenerate with Gemini"
                              >
                                <Sparkles className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => setDeletingQuestion(q)}
                                className="p-1.5 rounded-lg bg-red-950/40 hover:bg-red-950/70 border border-red-900/50 text-red-400"
                                title="Delete Question"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ==================== 4. FEEDBACK TAB ==================== */}
          {activeTab === 'feedback' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {['ALL', 'new', 'reviewed', 'resolved'].map((st) => (
                    <button
                      key={st}
                      onClick={() => setFeedbackStatusFilter(st)}
                      className={`px-3 py-1.5 rounded-xl text-xs font-bold uppercase transition-colors ${
                        feedbackStatusFilter === st
                          ? 'bg-[#F97316] text-[#0A0A0A]'
                          : 'bg-[#141414] border border-[#262626] text-[#A3A3A3]'
                      }`}
                    >
                      {st}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                {feedbackList.length === 0 ? (
                  <div className="bg-[#141414] border border-[#262626] rounded-2xl p-10 text-center text-xs text-[#737373]">
                    No feedback entries found.
                  </div>
                ) : (
                  feedbackList.map((fb) => (
                    <div
                      key={fb.id}
                      className="bg-[#141414] border border-[#262626] rounded-2xl p-5 space-y-3"
                    >
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <span
                            className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                              fb.status === 'resolved'
                                ? 'bg-[#10B981]/20 text-[#10B981]'
                                : fb.status === 'reviewed'
                                ? 'bg-[#F59E0B]/20 text-[#F59E0B]'
                                : 'bg-[#F97316]/20 text-[#F97316]'
                            }`}
                          >
                            {fb.status || 'new'}
                          </span>
                          <span className="px-2 py-0.5 rounded bg-[#1C1C1C] text-[#D4D4D4] text-[10px] uppercase font-mono">
                            {fb.category}
                          </span>
                          <span className="text-xs text-[#E5E5E5] font-semibold">
                            {fb.email || 'Anonymous Scholar'}
                          </span>
                          <span className="text-xs text-[#737373]">
                            • {new Date(fb.createdAt).toLocaleString()}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <select
                            value={fb.status || 'new'}
                            onChange={(e) =>
                              handleFeedbackAction(
                                fb.id,
                                'mark_status',
                                e.target.value as any
                              )
                            }
                            className="px-2.5 py-1.5 rounded-lg bg-[#1C1C1C] border border-[#262626] text-xs text-[#E5E5E5]"
                          >
                            <option value="new">Mark: New</option>
                            <option value="reviewed">Mark: Reviewed</option>
                            <option value="resolved">Mark: Resolved</option>
                          </select>
                          {fb.email && (
                            <button
                              onClick={() => {
                                setReplyModalFeedback(fb);
                                setReplyText(fb.adminReply || '');
                              }}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#F97316] text-[#0A0A0A] text-xs font-bold"
                            >
                              <Mail className="w-3.5 h-3.5" /> Reply by Email
                            </button>
                          )}
                        </div>
                      </div>
                      <p className="text-sm text-[#F5F5F5] whitespace-pre-wrap">
                        {fb.message}
                      </p>
                      {fb.adminReply && (
                        <div className="p-3 rounded-xl bg-[#1C1C1C] border border-[#262626] text-xs text-[#A3A3A3]">
                          <strong className="text-[#10B981] block mb-1">
                            Replied via Email:
                          </strong>
                          {fb.adminReply}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* ==================== 5. LIVE EVENTS & TOURNAMENTS TAB ==================== */}
          {activeTab === 'events' && (
            <div className="space-y-6">
              {/* LIVE Events */}
              <div className="bg-[#141414] border border-[#262626] rounded-2xl overflow-hidden">
                <div className="px-5 py-4 border-b border-[#262626]">
                  <h3 className="text-sm font-bold text-[#F5F5F5]">
                    LIVE Sanctuary Events
                  </h3>
                  <p className="text-xs text-[#A3A3A3]">
                    Manage active and past big-screen church quiz events
                  </p>
                </div>
                <div className="divide-y divide-[#262626]">
                  {eventsData.liveEvents.length === 0 ? (
                    <div className="p-8 text-center text-xs text-[#737373]">
                      No LIVE events created yet.
                    </div>
                  ) : (
                    eventsData.liveEvents.map((ev) => (
                      <div
                        key={ev.roomPin}
                        className="p-4 flex flex-wrap items-center justify-between gap-4"
                      >
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-bold text-[#F97316] text-xs">
                              PIN: {ev.roomPin}
                            </span>
                            <span className="text-sm font-bold text-[#F5F5F5]">
                              {ev.title}
                            </span>
                            <span
                              className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                                ev.status === 'ended'
                                  ? 'bg-[#262626] text-[#A3A3A3]'
                                  : 'bg-[#10B981]/20 text-[#10B981]'
                              }`}
                            >
                              {ev.status}
                            </span>
                          </div>
                          <p className="text-xs text-[#A3A3A3] mt-1">
                            Host: {ev.hostName} • Org: {ev.organization} • Participants:{' '}
                            {(ev.participants || []).length}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() =>
                              setViewingParticipantsEvent({
                                type: 'live',
                                title: ev.title,
                                roomPin: ev.roomPin,
                                participants: ev.participants || [],
                              })
                            }
                            className="px-3 py-1.5 rounded-lg bg-[#1C1C1C] hover:bg-[#262626] border border-[#262626] text-xs font-semibold text-[#E5E5E5]"
                          >
                            Participants ({(ev.participants || []).length})
                          </button>
                          {ev.status !== 'ended' && (
                            <button
                              onClick={() =>
                                handleEventAction('close_live_event', {
                                  roomPin: ev.roomPin,
                                  eventId: ev.id,
                                })
                              }
                              className="px-3 py-1.5 rounded-lg bg-red-950/50 hover:bg-red-950/80 border border-red-900/50 text-xs font-semibold text-red-400"
                            >
                              Close / End Event
                            </button>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Community Tournaments */}
              <div className="bg-[#141414] border border-[#262626] rounded-2xl overflow-hidden">
                <div className="px-5 py-4 border-b border-[#262626]">
                  <h3 className="text-sm font-bold text-[#F5F5F5]">
                    Community Tournaments
                  </h3>
                  <p className="text-xs text-[#A3A3A3]">
                    Inspect participants, close/cancel tournaments, or remove participants
                  </p>
                </div>
                <div className="divide-y divide-[#262626]">
                  {eventsData.tournaments.length === 0 ? (
                    <div className="p-8 text-center text-xs text-[#737373]">
                      No tournaments found.
                    </div>
                  ) : (
                    eventsData.tournaments.map((t) => (
                      <div
                        key={t.id}
                        className="p-4 flex flex-wrap items-center justify-between gap-4"
                      >
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-bold text-[#F5F5F5]">
                              {t.title}
                            </span>
                            <span
                              className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                                t.status === 'cancelled' || t.status === 'closed'
                                  ? 'bg-[#262626] text-[#A3A3A3]'
                                  : 'bg-[#F59E0B]/20 text-[#F59E0B]'
                              }`}
                            >
                              {t.status || 'open'}
                            </span>
                          </div>
                          <p className="text-xs text-[#A3A3A3] mt-1">
                            {t.organization} • {t.category} ({t.difficulty}) •{' '}
                            {(t.participants || []).length} enrolled
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() =>
                              setViewingParticipantsEvent({
                                type: 'tournament',
                                title: t.title,
                                eventId: t.id,
                                participants: t.participants || [],
                              })
                            }
                            className="px-3 py-1.5 rounded-lg bg-[#1C1C1C] hover:bg-[#262626] border border-[#262626] text-xs font-semibold text-[#E5E5E5]"
                          >
                            Participants ({(t.participants || []).length})
                          </button>
                          {t.status !== 'cancelled' && (
                            <button
                              onClick={() =>
                                handleEventAction('cancel_tournament', {
                                  eventId: t.id,
                                })
                              }
                              className="px-3 py-1.5 rounded-lg bg-red-950/50 hover:bg-red-950/80 border border-red-900/50 text-xs font-semibold text-red-400"
                            >
                              Cancel Tournament
                            </button>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ==================== 6. COMMUNITY FEED TAB ==================== */}
          {activeTab === 'feed' && (
            <div className="space-y-4">
              <div className="bg-[#141414] border border-[#262626] rounded-2xl overflow-hidden">
                <div className="px-5 py-4 border-b border-[#262626]">
                  <h3 className="text-sm font-bold text-[#F5F5F5]">
                    Community Feed Posts ({feedData.posts.length})
                  </h3>
                  <p className="text-xs text-[#A3A3A3]">
                    Moderate scholar milestone posts, remove inappropriate content, or ban spammers
                  </p>
                </div>
                <div className="divide-y divide-[#262626]">
                  {feedData.posts.length === 0 ? (
                    <div className="p-8 text-center text-xs text-[#737373]">
                      No community posts found.
                    </div>
                  ) : (
                    feedData.posts.map((post) => (
                      <div
                        key={post.id}
                        className="p-4 flex flex-wrap items-start justify-between gap-4"
                      >
                        <div className="space-y-1 max-w-2xl">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-bold text-[#F97316]">
                              {post.username}
                            </span>
                            <span className="text-[11px] font-mono text-[#737373]">
                              ({post.userUid})
                            </span>
                            <span className="text-[11px] text-[#737373]">
                              • {new Date(post.createdAt).toLocaleString()}
                            </span>
                          </div>
                          <p className="text-sm text-[#F5F5F5]">{post.content}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleFeedAction('delete_post', post.id)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#1C1C1C] hover:bg-[#262626] border border-[#262626] text-xs font-semibold text-[#E5E5E5]"
                          >
                            <Trash2 className="w-3.5 h-3.5 text-red-400" /> Delete Post
                          </button>
                          <button
                            onClick={() =>
                              setBanPosterModal({
                                posterUid: post.userUid,
                                username: post.username,
                                postId: post.id,
                              })
                            }
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-red-950/50 hover:bg-red-950/80 border border-red-900/50 text-xs font-semibold text-red-400"
                          >
                            <Ban className="w-3.5 h-3.5" /> Ban Poster
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ==================== 7. SETTINGS TAB ==================== */}
          {activeTab === 'settings' && (
            <div className="max-w-2xl space-y-6">
              <form
                onSubmit={handleSaveSettings}
                className="bg-[#141414] border border-[#262626] rounded-2xl p-6 space-y-5"
              >
                <div>
                  <h3 className="text-base font-bold text-[#F5F5F5]">
                    Global Platform Controls
                  </h3>
                  <p className="text-xs text-[#A3A3A3]">
                    Changes take effect immediately across Quick!Bible
                  </p>
                </div>

                {/* Maintenance Mode Toggle */}
                <div className="flex items-center justify-between p-4 rounded-xl bg-[#1C1C1C] border border-[#262626]">
                  <div>
                    <div className="text-sm font-bold text-[#F5F5F5]">
                      Maintenance Mode
                    </div>
                    <p className="text-xs text-[#A3A3A3]">
                      Displays a global maintenance banner and blocks non-admin gameplay
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      setSettingsForm((s) => ({
                        ...s,
                        maintenanceMode: !s.maintenanceMode,
                      }))
                    }
                    className={`w-12 h-6 rounded-full transition-colors p-0.5 ${
                      settingsForm.maintenanceMode ? 'bg-[#F97316]' : 'bg-[#262626]'
                    }`}
                  >
                    <div
                      className={`w-5 h-5 rounded-full bg-[#0A0A0A] transition-transform ${
                        settingsForm.maintenanceMode ? 'translate-x-6' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>

                {/* Enable / Disable Signups Toggle */}
                <div className="flex items-center justify-between p-4 rounded-xl bg-[#1C1C1C] border border-[#262626]">
                  <div>
                    <div className="text-sm font-bold text-[#F5F5F5]">
                      Allow New Account Signups
                    </div>
                    <p className="text-xs text-[#A3A3A3]">
                      When disabled, new user registrations are blocked server-side
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      setSettingsForm((s) => ({
                        ...s,
                        signupsEnabled: !s.signupsEnabled,
                      }))
                    }
                    className={`w-12 h-6 rounded-full transition-colors p-0.5 ${
                      settingsForm.signupsEnabled ? 'bg-[#10B981]' : 'bg-[#262626]'
                    }`}
                  >
                    <div
                      className={`w-5 h-5 rounded-full bg-[#0A0A0A] transition-transform ${
                        settingsForm.signupsEnabled ? 'translate-x-6' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>

                {/* Announcement Banner */}
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-[#A3A3A3] mb-2">
                    Global Announcement Banner Text
                  </label>
                  <input
                    type="text"
                    value={settingsForm.announcementBanner}
                    onChange={(e) =>
                      setSettingsForm((s) => ({
                        ...s,
                        announcementBanner: e.target.value,
                      }))
                    }
                    placeholder="Leave empty to hide banner (e.g. 'National Youth Bible Quiz Finals starts at 7 PM!')"
                    className="w-full px-4 py-2.5 rounded-xl bg-[#0A0A0A] border border-[#262626] text-xs text-[#F5F5F5] placeholder-[#737373] focus:outline-none focus:border-[#F97316]"
                  />
                </div>

                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={savingSettings}
                    className="px-5 py-2.5 rounded-xl bg-[#F97316] hover:bg-[#EA580C] text-[#0A0A0A] font-extrabold text-xs transition-colors"
                  >
                    {savingSettings ? 'Saving Settings...' : 'Save Platform Settings'}
                  </button>
                </div>
              </form>

              {/* SQL Security Reference Box */}
              <div className="bg-[#141414] border border-[#262626] rounded-2xl p-5 space-y-2">
                <div className="flex items-center gap-2 text-xs font-bold text-[#F59E0B]">
                  <Lock className="w-4 h-4" />
                  <span>Supabase SQL Admin Promotion Command</span>
                </div>
                <p className="text-xs text-[#A3A3A3]">
                  Self-service role updates are blocked by RLS & trigger (`trg_prevent_profile_role_tampering`). Run this in the Supabase SQL Editor to promote an account manually:
                </p>
                <pre className="p-3 rounded-xl bg-[#0A0A0A] border border-[#262626] text-[11px] font-mono text-[#10B981] overflow-x-auto">
                  {`UPDATE public.profiles SET role = 'admin' WHERE email = 'adonizchoy@gmail.com';`}
                </pre>
              </div>
            </div>
          )}

          {/* ==================== 8. QUESTION BANK & 66-BOOK COVERAGE TAB ==================== */}
          {activeTab === 'question-bank' && (
            <div className="space-y-6">
              {/* Top Sub-Navigation & Action Bar */}
              <div className="flex flex-wrap items-center justify-between gap-3 bg-[#141414] border border-[#262626] rounded-2xl p-4">
                <div className="flex flex-wrap gap-2">
                  {[
                    { id: 'inventory', label: '1. Overall Inventory' },
                    { id: 'coverage', label: '2. 66-Book Coverage Registry' },
                    { id: 'analytics', label: '3. Question Analytics & Logs' },
                    { id: 'controls', label: '4. Admin Controls & Verification' },
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setQbSubTab(tab.id as any)}
                      className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-colors ${
                        qbSubTab === tab.id
                          ? 'bg-[#F97316] text-[#0A0A0A]'
                          : 'bg-[#1C1C1C] text-[#A3A3A3] hover:text-[#F5F5F5]'
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    disabled={qbBusy}
                    onClick={handleTriggerReplenishment}
                    className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-[#10B981] hover:bg-[#059669] text-[#0A0A0A] text-xs font-extrabold disabled:opacity-50"
                  >
                    <Sparkles className="w-3.5 h-3.5" />
                    {qbBusy ? 'Working...' : 'Replenish Inventory Now'}
                  </button>
                  <button
                    onClick={loadQuestionBank}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-[#1C1C1C] hover:bg-[#262626] border border-[#262626] text-xs font-semibold text-[#E5E5E5]"
                  >
                    <RefreshCw className="w-3.5 h-3.5" /> Refresh
                  </button>
                </div>
              </div>

              {!qbOverview ? (
                <div className="p-12 text-center bg-[#141414] border border-[#262626] rounded-2xl text-xs text-[#A3A3A3]">
                  Loading 66-Book Bible Question Bank & WEB Verification Engine...
                </div>
              ) : (
                <>
                  {/* 1. OVERALL INVENTORY */}
                  {qbSubTab === 'inventory' && (
                    <div className="space-y-6">
                      {/* Target Progress Bar */}
                      <div className="bg-[#141414] border border-[#262626] rounded-2xl p-5 space-y-3">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div>
                            <h3 className="text-sm font-extrabold text-[#F5F5F5]">
                              Verified Scripture Inventory Target (World English Bible Grounded)
                            </h3>
                            <p className="text-xs text-[#A3A3A3]">
                              Active gameplay serves strictly verified questions across all 66 canonical books without real-time AI calls.
                            </p>
                          </div>
                          <div className="text-right">
                            <span className="text-lg font-extrabold text-[#F97316] font-mono">
                              {qbOverview.overallInventory.verifiedQuestions.toLocaleString()}
                            </span>
                            <span className="text-xs text-[#737373] font-mono">
                              {' '}
                              / {qbOverview.overallInventory.targetVerifiedQuestions.toLocaleString()} target ({qbOverview.overallInventory.targetProgressPercent}%)
                            </span>
                          </div>
                        </div>
                        <div className="w-full h-3 rounded-full bg-[#0A0A0A] border border-[#262626] overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-[#F97316] to-[#10B981] transition-all duration-500"
                            style={{
                              width: `${Math.max(2, Math.min(100, qbOverview.overallInventory.targetProgressPercent))}%`,
                            }}
                          />
                        </div>
                      </div>

                      {/* Inventory KPI Grid */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
                        {[
                          {
                            label: 'Total Questions',
                            value: qbOverview.overallInventory.totalQuestions,
                            color: 'text-[#F5F5F5]',
                          },
                          {
                            label: 'Verified (WEB)',
                            value: qbOverview.overallInventory.verifiedQuestions,
                            color: 'text-[#10B981]',
                          },
                          {
                            label: 'Pending Review',
                            value: qbOverview.overallInventory.pendingVerification,
                            color: 'text-[#F59E0B]',
                          },
                          {
                            label: 'Rejected',
                            value: qbOverview.overallInventory.rejectedQuestions,
                            color: 'text-red-400',
                          },
                          {
                            label: 'English (EN)',
                            value: qbOverview.overallInventory.englishQuestions,
                            color: 'text-[#F97316]',
                          },
                          {
                            label: 'Filipino (TL)',
                            value: qbOverview.overallInventory.filipinoQuestions,
                            color: 'text-[#38BDF8]',
                          },
                          {
                            label: 'Cebuano (CEB)',
                            value: qbOverview.overallInventory.cebuanoQuestions,
                            color: 'text-[#A78BFA]',
                          },
                        ].map((card, i) => (
                          <div
                            key={i}
                            className="bg-[#141414] border border-[#262626] rounded-2xl p-4 space-y-1"
                          >
                            <div className="text-[11px] font-bold uppercase tracking-wider text-[#737373]">
                              {card.label}
                            </div>
                            <div className={`text-xl font-extrabold font-mono ${card.color}`}>
                              {Number(card.value || 0).toLocaleString()}
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Summary of 66-Book Canon Coverage */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="bg-[#141414] border border-[#262626] rounded-2xl p-5 space-y-2">
                          <div className="text-xs font-bold uppercase text-[#A3A3A3]">
                            Complete Canon Coverage (66 Books)
                          </div>
                          <div className="text-2xl font-extrabold text-[#10B981] font-mono">
                            {qbOverview.bibleCoverage.booksCoveredCount} / 66 Books
                          </div>
                          <p className="text-xs text-[#737373]">
                            Chapters Covered: {qbOverview.bibleCoverage.totalChaptersCovered} / {qbOverview.bibleCoverage.totalCanonicalChapters} canonical chapters
                          </p>
                        </div>

                        <div className="bg-[#141414] border border-[#262626] rounded-2xl p-5 space-y-2">
                          <div className="text-xs font-bold uppercase text-[#A3A3A3]">
                            Old Testament (39 Books)
                          </div>
                          <div className="text-2xl font-extrabold text-[#F97316] font-mono">
                            {qbOverview.bibleCoverage.oldTestamentCoveredCount} / 39 Books
                          </div>
                          <p className="text-xs text-[#737373]">
                            Genesis through Malachi verified in WEB canon
                          </p>
                        </div>

                        <div className="bg-[#141414] border border-[#262626] rounded-2xl p-5 space-y-2">
                          <div className="text-xs font-bold uppercase text-[#A3A3A3]">
                            New Testament (27 Books)
                          </div>
                          <div className="text-2xl font-extrabold text-[#38BDF8] font-mono">
                            {qbOverview.bibleCoverage.newTestamentCoveredCount} / 27 Books
                          </div>
                          <p className="text-xs text-[#737373]">
                            Matthew through Revelation verified in WEB canon
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* 2. 66-BOOK COVERAGE REGISTRY */}
                  {qbSubTab === 'coverage' && (
                    <div className="space-y-4">
                      <div className="flex flex-wrap items-center justify-between gap-3 bg-[#141414] border border-[#262626] rounded-2xl p-4">
                        <div className="flex flex-wrap items-center gap-2">
                          {[
                            { id: 'ALL', label: `All Books (66)` },
                            { id: 'OT', label: `Old Testament (39)` },
                            { id: 'NT', label: `New Testament (27)` },
                            {
                              id: 'LOW',
                              label: `Below Min Target (${(qbOverview.bibleCoverage.booksWithInsufficientCoverage || []).length})`,
                            },
                            {
                              id: 'ZERO',
                              label: `Zero Questions (${(qbOverview.bibleCoverage.booksWithZeroQuestions || []).length})`,
                            },
                          ].map((f) => (
                            <button
                              key={f.id}
                              onClick={() => setQbBookFilter(f.id as any)}
                              className={`px-3 py-1.5 rounded-xl text-xs font-bold ${
                                qbBookFilter === f.id
                                  ? 'bg-[#F97316] text-[#0A0A0A]'
                                  : 'bg-[#1C1C1C] text-[#A3A3A3]'
                              }`}
                            >
                              {f.label}
                            </button>
                          ))}
                        </div>
                        <div className="relative w-full sm:w-64">
                          <Search className="w-3.5 h-3.5 text-[#737373] absolute left-3 top-1/2 -translate-y-1/2" />
                          <input
                            type="text"
                            value={qbBookSearch}
                            onChange={(e) => setQbBookSearch(e.target.value)}
                            placeholder="Filter book name (e.g. Obadiah, Jude)..."
                            className="w-full pl-8 pr-3 py-1.5 rounded-xl bg-[#0A0A0A] border border-[#262626] text-xs text-[#F5F5F5]"
                          />
                        </div>
                      </div>

                      <div className="bg-[#141414] border border-[#262626] rounded-2xl overflow-hidden">
                        <div className="overflow-x-auto max-h-[560px]">
                          <table className="w-full text-left border-collapse text-xs">
                            <thead className="bg-[#1C1C1C] text-[#A3A3A3] uppercase text-[10px] sticky top-0 z-10">
                              <tr>
                                <th className="p-3">#</th>
                                <th className="p-3">Book</th>
                                <th className="p-3">Testament</th>
                                <th className="p-3">Chapters Covered</th>
                                <th className="p-3">Verified / Total</th>
                                <th className="p-3">Difficulty (E / M / H)</th>
                                <th className="p-3">Languages (EN / TL / CEB)</th>
                                <th className="p-3 text-right">Actions</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-[#262626]">
                              {(qbOverview.bibleCoverage.books || [])
                                .filter((b: any) => {
                                  if (qbBookFilter === 'OT' && b.testament !== 'Old Testament')
                                    return false;
                                  if (qbBookFilter === 'NT' && b.testament !== 'New Testament')
                                    return false;
                                  if (
                                    qbBookFilter === 'LOW' &&
                                    b.totalVerifiedQuestions >= (qbOverview.config?.minQuestionsPerBook || 10)
                                  )
                                    return false;
                                  if (qbBookFilter === 'ZERO' && b.totalVerifiedQuestions > 0)
                                    return false;
                                  if (
                                    qbBookSearch.trim() &&
                                    !b.bookName
                                      .toLowerCase()
                                      .includes(qbBookSearch.trim().toLowerCase())
                                  )
                                    return false;
                                  return true;
                                })
                                .map((b: any) => (
                                  <tr
                                    key={b.bookOrder}
                                    className="hover:bg-[#1C1C1C]/60 transition-colors"
                                  >
                                    <td className="p-3 font-mono text-[#737373]">{b.bookOrder}</td>
                                    <td className="p-3 font-bold text-[#F5F5F5]">{b.bookName}</td>
                                    <td className="p-3">
                                      <span
                                        className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                                          b.testament === 'Old Testament'
                                            ? 'bg-[#F97316]/15 text-[#F97316]'
                                            : 'bg-[#38BDF8]/15 text-[#38BDF8]'
                                        }`}
                                      >
                                        {b.testament}
                                      </span>
                                    </td>
                                    <td className="p-3 font-mono text-[#E5E5E5]">
                                      {b.chaptersCoveredCount} / {b.totalChapters} ch
                                    </td>
                                    <td className="p-3 font-mono">
                                      <span className="text-[#10B981] font-bold">
                                        {b.totalVerifiedQuestions}
                                      </span>{' '}
                                      <span className="text-[#737373]">
                                        / {b.totalGeneratedQuestions}
                                      </span>
                                    </td>
                                    <td className="p-3 font-mono text-[#A3A3A3]">
                                      {b.difficultyDistribution.Easy} /{' '}
                                      {b.difficultyDistribution.Medium} /{' '}
                                      {b.difficultyDistribution.Hard}
                                    </td>
                                    <td className="p-3 font-mono text-[#A3A3A3]">
                                      {b.languageCoverage.en} / {b.languageCoverage.tl} /{' '}
                                      {b.languageCoverage.ceb}
                                    </td>
                                    <td className="p-3 text-right space-x-1.5">
                                      <button
                                        onClick={() => setSelectedBookDetail(b)}
                                        className="px-2.5 py-1 rounded-lg bg-[#1C1C1C] hover:bg-[#262626] border border-[#262626] text-[#E5E5E5] font-semibold"
                                      >
                                        Chapters
                                      </button>
                                      <button
                                        disabled={qbBusy}
                                        onClick={() => handleGenerateQbBatch(b.bookName)}
                                        className="px-2.5 py-1 rounded-lg bg-[#F97316]/20 hover:bg-[#F97316]/30 border border-[#F97316]/40 text-[#F97316] font-bold disabled:opacity-50"
                                      >
                                        + Generate
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* 3. QUESTION ANALYTICS & LOGS */}
                  {qbSubTab === 'analytics' && (
                    <div className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* By Category */}
                        <div className="bg-[#141414] border border-[#262626] rounded-2xl p-5 space-y-3">
                          <h4 className="text-xs font-bold uppercase tracking-wider text-[#A3A3A3]">
                            Questions by Canonical Category
                          </h4>
                          <div className="space-y-2 max-h-64 overflow-y-auto pr-1 text-xs">
                            {Object.entries(qbOverview.analytics.questionsByCategory || {}).map(
                              ([cat, count]: [string, any]) => (
                                <div
                                  key={cat}
                                  className="flex items-center justify-between py-1.5 border-b border-[#262626]/60"
                                >
                                  <span className="text-[#E5E5E5]">{cat}</span>
                                  <span className="font-mono font-bold text-[#F97316]">
                                    {count}
                                  </span>
                                </div>
                              )
                            )}
                          </div>
                        </div>

                        {/* By Difficulty & Language */}
                        <div className="bg-[#141414] border border-[#262626] rounded-2xl p-5 space-y-4">
                          <div>
                            <h4 className="text-xs font-bold uppercase tracking-wider text-[#A3A3A3] mb-2">
                              Questions by Difficulty (3-Tier)
                            </h4>
                            <div className="space-y-1.5 text-xs">
                              {Object.entries(qbOverview.analytics.questionsByDifficulty || {}).map(
                                ([diff, count]: [string, any]) => (
                                  <div
                                    key={diff}
                                    className="flex items-center justify-between py-1 border-b border-[#262626]/60"
                                  >
                                    <span className="text-[#E5E5E5]">{diff}</span>
                                    <span className="font-mono font-bold text-[#10B981]">
                                      {count}
                                    </span>
                                  </div>
                                )
                              )}
                            </div>
                          </div>

                          <div>
                            <h4 className="text-xs font-bold uppercase tracking-wider text-[#A3A3A3] mb-2">
                              Multilingual Translations (Shared Question ID)
                            </h4>
                            <div className="space-y-1.5 text-xs">
                              <div className="flex justify-between">
                                <span className="text-[#A3A3A3]">English (Default)</span>
                                <span className="font-mono font-bold text-[#F5F5F5]">
                                  {qbOverview.analytics.questionsByLanguage?.en || 0}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-[#A3A3A3]">Filipino (Tagalog)</span>
                                <span className="font-mono font-bold text-[#38BDF8]">
                                  {qbOverview.analytics.questionsByLanguage?.tl || 0}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-[#A3A3A3]">Cebuano (Bisaya)</span>
                                <span className="font-mono font-bold text-[#A78BFA]">
                                  {qbOverview.analytics.questionsByLanguage?.ceb || 0}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Verification Quality Metrics */}
                        <div className="bg-[#141414] border border-[#262626] rounded-2xl p-5 space-y-3">
                          <h4 className="text-xs font-bold uppercase tracking-wider text-[#A3A3A3]">
                            11-Step WEB Verification Quality
                          </h4>
                          <div className="p-3 rounded-xl bg-[#1C1C1C] border border-[#262626] flex items-center justify-between">
                            <span className="text-xs text-[#A3A3A3]">Verification Pass Rate</span>
                            <span className="text-lg font-extrabold font-mono text-[#10B981]">
                              {qbOverview.analytics.verificationSuccessRate}%
                            </span>
                          </div>
                          <div className="p-3 rounded-xl bg-[#1C1C1C] border border-[#262626] flex items-center justify-between">
                            <span className="text-xs text-[#A3A3A3]">Duplicate Rejections</span>
                            <span className="text-lg font-extrabold font-mono text-[#F59E0B]">
                              {qbOverview.analytics.duplicateRejections}
                            </span>
                          </div>
                          <p className="text-[11px] text-[#737373]">
                            Every generated item is checked against World English Bible (WEB) scripture passages before receiving verified status.
                          </p>
                        </div>
                      </div>

                      {/* Recent Generation Batches */}
                      <div className="bg-[#141414] border border-[#262626] rounded-2xl p-5 space-y-3">
                        <h4 className="text-xs font-bold uppercase tracking-wider text-[#A3A3A3]">
                          Recent Generation Batches & Inventory Jobs
                        </h4>
                        <div className="overflow-x-auto max-h-64">
                          <table className="w-full text-left text-xs">
                            <thead className="text-[10px] uppercase text-[#737373] border-b border-[#262626]">
                              <tr>
                                <th className="py-2">Job ID</th>
                                <th className="py-2">Category / Book</th>
                                <th className="py-2">Difficulty</th>
                                <th className="py-2">Status</th>
                                <th className="py-2">Completed</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-[#262626]">
                              {(qbOverview.analytics.recentGenerationBatches || []).length === 0 ? (
                                <tr>
                                  <td colSpan={5} className="py-6 text-center text-[#737373]">
                                    No batch jobs recorded yet. Use Admin Controls to generate a batch.
                                  </td>
                                </tr>
                              ) : (
                                (qbOverview.analytics.recentGenerationBatches || []).map(
                                  (job: any) => (
                                    <tr key={job.id}>
                                      <td className="py-2 font-mono text-[#737373]">#{job.id}</td>
                                      <td className="py-2 font-bold text-[#F5F5F5]">
                                        {job.targetCategory}
                                      </td>
                                      <td className="py-2 text-[#A3A3A3]">{job.targetDifficulty}</td>
                                      <td className="py-2">
                                        <span
                                          className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                                            job.status === 'completed'
                                              ? 'bg-[#10B981]/15 text-[#10B981]'
                                              : 'bg-[#F59E0B]/15 text-[#F59E0B]'
                                          }`}
                                        >
                                          {job.status}
                                        </span>
                                      </td>
                                      <td className="py-2 font-mono text-[#737373]">
                                        {job.completedAt
                                          ? new Date(job.completedAt).toLocaleString()
                                          : 'Running'}
                                      </td>
                                    </tr>
                                  )
                                )
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* 4. ADMINISTRATIVE CONTROLS & VERIFICATION REVIEW */}
                  {qbSubTab === 'controls' && (
                    <div className="space-y-6">
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Generate New Question Batch */}
                        <div className="bg-[#141414] border border-[#262626] rounded-2xl p-5 space-y-4">
                          <div>
                            <h3 className="text-sm font-extrabold text-[#F5F5F5]">
                              Generate & Verify New Question Batch (EN / TL / CEB)
                            </h3>
                            <p className="text-xs text-[#A3A3A3]">
                              Generates questions grounded in the World English Bible (WEB) and runs the 11-step verification pipeline.
                            </p>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                            <div>
                              <label className="block font-bold text-[#A3A3A3] mb-1">
                                Target Canonical Book (66 Books)
                              </label>
                              <select
                                value={qbBatchForm.book}
                                onChange={(e) =>
                                  setQbBatchForm((s) => ({ ...s, book: e.target.value }))
                                }
                                className="w-full px-3 py-2 rounded-xl bg-[#0A0A0A] border border-[#262626] text-[#F5F5F5]"
                              >
                                {(qbOverview.bibleCoverage.books || []).map((b: any) => (
                                  <option key={b.bookOrder} value={b.bookName}>
                                    {b.bookOrder}. {b.bookName} ({b.totalVerifiedQuestions} verified)
                                  </option>
                                ))}
                              </select>
                            </div>

                            <div>
                              <label className="block font-bold text-[#A3A3A3] mb-1">
                                Canonical Category
                              </label>
                              <select
                                value={qbBatchForm.category}
                                onChange={(e) =>
                                  setQbBatchForm((s) => ({ ...s, category: e.target.value }))
                                }
                                className="w-full px-3 py-2 rounded-xl bg-[#0A0A0A] border border-[#262626] text-[#F5F5F5]"
                              >
                                {[
                                  'Old Testament',
                                  'New Testament',
                                  'Bible Characters',
                                  'Bible Events',
                                  'Bible Geography',
                                  'Bible History',
                                  'Biblical Teachings',
                                  'Biblical Knowledge',
                                  'Advanced Biblical Studies',
                                  'Prophecies and Fulfillments',
                                ].map((cat) => (
                                  <option key={cat} value={cat}>
                                    {cat}
                                  </option>
                                ))}
                              </select>
                            </div>

                            <div>
                              <label className="block font-bold text-[#A3A3A3] mb-1">
                                Difficulty Level
                              </label>
                              <select
                                value={qbBatchForm.difficulty}
                                onChange={(e) =>
                                  setQbBatchForm((s) => ({
                                    ...s,
                                    difficulty: e.target.value as any,
                                  }))
                                }
                                className="w-full px-3 py-2 rounded-xl bg-[#0A0A0A] border border-[#262626] text-[#F5F5F5]"
                              >
                                <option value="Easy">Easy</option>
                                <option value="Medium">Medium</option>
                                <option value="Hard">Hard</option>
                              </select>
                            </div>

                            <div>
                              <label className="block font-bold text-[#A3A3A3] mb-1">
                                Batch Size (Questions)
                              </label>
                              <input
                                type="number"
                                min={1}
                                max={25}
                                value={qbBatchForm.count}
                                onChange={(e) =>
                                  setQbBatchForm((s) => ({
                                    ...s,
                                    count: Math.max(1, Math.min(25, Number(e.target.value) || 5)),
                                  }))
                                }
                                className="w-full px-3 py-2 rounded-xl bg-[#0A0A0A] border border-[#262626] text-[#F5F5F5]"
                              />
                            </div>
                          </div>

                          <button
                            disabled={qbBusy}
                            onClick={() => handleGenerateQbBatch()}
                            className="w-full py-2.5 rounded-xl bg-[#F97316] hover:bg-[#EA580C] text-[#0A0A0A] font-extrabold text-xs disabled:opacity-50"
                          >
                            {qbBusy
                              ? 'Generating & Running 11-Step WEB Verification...'
                              : `Generate Batch for ${qbBatchForm.book}`}
                          </button>
                        </div>

                        {/* Configure Target Inventory Settings */}
                        <form
                          onSubmit={handleSaveQbConfig}
                          className="bg-[#141414] border border-[#262626] rounded-2xl p-5 space-y-4"
                        >
                          <div>
                            <h3 className="text-sm font-extrabold text-[#F5F5F5]">
                              Configure Target Inventory & Replenishment Settings
                            </h3>
                            <p className="text-xs text-[#A3A3A3]">
                              Controls automated background replenishment targets across all 66 books.
                            </p>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                            <div>
                              <label className="block font-bold text-[#A3A3A3] mb-1">
                                Target Verified Total
                              </label>
                              <input
                                type="number"
                                min={100}
                                max={100000}
                                value={qbConfigForm.targetVerifiedQuestions}
                                onChange={(e) =>
                                  setQbConfigForm((s) => ({
                                    ...s,
                                    targetVerifiedQuestions: Number(e.target.value) || 5000,
                                  }))
                                }
                                className="w-full px-3 py-2 rounded-xl bg-[#0A0A0A] border border-[#262626] text-[#F5F5F5]"
                              />
                            </div>

                            <div>
                              <label className="block font-bold text-[#A3A3A3] mb-1">
                                Replenish Batch Size
                              </label>
                              <input
                                type="number"
                                min={5}
                                max={2000}
                                value={qbConfigForm.replenishmentBatchSize}
                                onChange={(e) =>
                                  setQbConfigForm((s) => ({
                                    ...s,
                                    replenishmentBatchSize: Number(e.target.value) || 500,
                                  }))
                                }
                                className="w-full px-3 py-2 rounded-xl bg-[#0A0A0A] border border-[#262626] text-[#F5F5F5]"
                              />
                            </div>

                            <div>
                              <label className="block font-bold text-[#A3A3A3] mb-1">
                                Min Questions / Book
                              </label>
                              <input
                                type="number"
                                min={1}
                                max={500}
                                value={qbConfigForm.minQuestionsPerBook}
                                onChange={(e) =>
                                  setQbConfigForm((s) => ({
                                    ...s,
                                    minQuestionsPerBook: Number(e.target.value) || 10,
                                  }))
                                }
                                className="w-full px-3 py-2 rounded-xl bg-[#0A0A0A] border border-[#262626] text-[#F5F5F5]"
                              />
                            </div>
                          </div>

                          <div className="flex items-center justify-between p-3 rounded-xl bg-[#1C1C1C] border border-[#262626] text-xs">
                            <span className="font-semibold text-[#E5E5E5]">
                              Enable Automated Background Replenishment
                            </span>
                            <input
                              type="checkbox"
                              checked={qbConfigForm.autoReplenishEnabled}
                              onChange={(e) =>
                                setQbConfigForm((s) => ({
                                  ...s,
                                  autoReplenishEnabled: e.target.checked,
                                }))
                              }
                              className=" accent-[#F97316] w-4 h-4"
                            />
                          </div>

                          <button
                            type="submit"
                            disabled={qbBusy}
                            className="w-full py-2.5 rounded-xl bg-[#1C1C1C] hover:bg-[#262626] border border-[#262626] text-[#F5F5F5] font-extrabold text-xs"
                          >
                            Save Inventory Target Configuration
                          </button>
                        </form>
                      </div>

                      {/* Pending Questions Queue & Recent Verified Inspection */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="bg-[#141414] border border-[#262626] rounded-2xl p-5 space-y-3">
                          <h4 className="text-xs font-bold uppercase tracking-wider text-[#F59E0B]">
                            Pending Manual Review Queue ({(qbOverview.pendingQuestionsQueue || []).length})
                          </h4>
                          <div className="divide-y divide-[#262626] max-h-72 overflow-y-auto text-xs">
                            {(qbOverview.pendingQuestionsQueue || []).length === 0 ? (
                              <div className="py-8 text-center text-[#737373]">
                                No pending questions awaiting manual verification.
                              </div>
                            ) : (
                              (qbOverview.pendingQuestionsQueue || []).map((q: any) => (
                                <div key={q.id} className="py-3 space-y-2">
                                  <div className="flex items-center justify-between">
                                    <span className="font-mono text-[#F97316] font-bold">
                                      #{q.id} • {q.scriptureReference}
                                    </span>
                                    <span className="text-[#737373]">{q.category}</span>
                                  </div>
                                  <p className="text-[#F5F5F5]">{q.questionText}</p>
                                  <div className="flex items-center justify-between pt-1">
                                    <span className="text-[#10B981] font-semibold">
                                      Answer: {q.correctAnswer}
                                    </span>
                                    <div className="flex gap-1.5">
                                      <button
                                        onClick={() => setInspectingQuestion(q)}
                                        className="px-2.5 py-1 rounded bg-[#1C1C1C] text-[#E5E5E5]"
                                      >
                                        Inspect WEB Check
                                      </button>
                                      <button
                                        disabled={qbBusy}
                                        onClick={() => handleReviewPendingQuestion(q.id, 'approve')}
                                        className="px-2.5 py-1 rounded bg-[#10B981]/20 text-[#10B981] font-bold"
                                      >
                                        Approve
                                      </button>
                                      <button
                                        disabled={qbBusy}
                                        onClick={() => handleReviewPendingQuestion(q.id, 'reject')}
                                        className="px-2.5 py-1 rounded bg-red-950/60 text-red-400 font-bold"
                                      >
                                        Reject
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                        </div>

                        <div className="bg-[#141414] border border-[#262626] rounded-2xl p-5 space-y-3">
                          <h4 className="text-xs font-bold uppercase tracking-wider text-[#10B981]">
                            Recent Verified Questions — Inspect WEB Verification Details
                          </h4>
                          <div className="divide-y divide-[#262626] max-h-72 overflow-y-auto text-xs">
                            {(qbOverview.recentVerifiedQuestions || []).map((q: any) => (
                              <div
                                key={q.id}
                                className="py-2.5 flex items-center justify-between gap-2"
                              >
                                <div className="min-w-0">
                                  <div className="font-mono text-[#F97316] font-bold">
                                    #{q.id} • {q.scriptureReference} ({q.book})
                                  </div>
                                  <div className="text-[#E5E5E5] truncate">{q.questionText}</div>
                                </div>
                                <button
                                  onClick={() => setInspectingQuestion(q)}
                                  className="shrink-0 px-2.5 py-1 rounded-lg bg-[#1C1C1C] hover:bg-[#262626] border border-[#262626] text-[#E5E5E5]"
                                >
                                  Inspect
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ==================== USER DETAIL DRAWER ==================== */}
      {selectedUserUid && (
        <div className="fixed inset-0 z-50 bg-black/75 flex justify-end">
          <div className="w-full max-w-xl bg-[#141414] border-l border-[#262626] h-full overflow-y-auto p-6 space-y-6">
            <div className="flex items-center justify-between border-b border-[#262626] pb-4">
              <div>
                <h3 className="text-base font-extrabold text-[#F5F5F5]">
                  Scholar Profile & Security Drawer
                </h3>
                <p className="text-xs font-mono text-[#A3A3A3]">{selectedUserUid}</p>
              </div>
              <button
                onClick={() => {
                  setSelectedUserUid(null);
                  setUserDetail(null);
                }}
                className="p-2 rounded-xl bg-[#1C1C1C] hover:bg-[#262626] text-[#A3A3A3]"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {loadingUserDetail || !userDetail ? (
              <div className="py-16 text-center text-xs text-[#A3A3A3]">
                Loading scholar profile, games, and audit history...
              </div>
            ) : (
              <>
                {/* Profile Summary */}
                <div className="bg-[#1C1C1C] border border-[#262626] rounded-2xl p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-base font-extrabold text-[#F5F5F5]">
                        {userDetail.user.username}
                      </div>
                      <div className="text-xs text-[#A3A3A3]">
                        {userDetail.user.email}
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="px-2.5 py-1 rounded-md bg-[#262626] text-[#F97316] text-xs font-bold uppercase">
                        Role: {userDetail.user.role || 'user'}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-2 pt-2 border-t border-[#262626] text-xs">
                    <div>
                      <span className="text-[#737373] block">Status</span>
                      <strong className="text-[#F5F5F5] uppercase">
                        {userDetail.user.status || 'active'}
                      </strong>
                    </div>
                    <div>
                      <span className="text-[#737373] block">Rating / XP</span>
                      <strong className="text-[#F59E0B]">
                        {userDetail.rating} MMR • {userDetail.stats?.xp || 0} XP
                      </strong>
                    </div>
                    <div>
                      <span className="text-[#737373] block">Games Played</span>
                      <strong className="text-[#10B981]">
                        {userDetail.stats?.gamesPlayed || 0}
                      </strong>
                    </div>
                  </div>
                </div>

                {/* Privileged Admin Actions */}
                <div className="space-y-2">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-[#A3A3A3]">
                    Privileged Actions (Verified Server-Side)
                  </h4>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {userDetail.user.status === 'banned' ? (
                      <button
                        onClick={() =>
                          setActionModal({
                            type: 'unban',
                            targetUser: userDetail.user,
                          })
                        }
                        className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-[#10B981]/20 border border-[#10B981]/40 text-[#10B981] text-xs font-bold"
                      >
                        <UserCheck className="w-3.5 h-3.5" /> Unban User
                      </button>
                    ) : (
                      <button
                        onClick={() =>
                          setActionModal({
                            type: 'ban',
                            targetUser: userDetail.user,
                          })
                        }
                        className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-red-950/50 border border-red-900/50 text-red-400 text-xs font-bold"
                      >
                        <Ban className="w-3.5 h-3.5" /> Ban User
                      </button>
                    )}

                    <button
                      onClick={() =>
                        setActionModal({
                          type: 'suspend',
                          targetUser: userDetail.user,
                        })
                      }
                      className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-[#1C1C1C] hover:bg-[#262626] border border-[#262626] text-[#F59E0B] text-xs font-semibold"
                    >
                      <Clock className="w-3.5 h-3.5" /> Suspend X Days
                    </button>

                    <button
                      onClick={() => {
                        setTargetRole(
                          userDetail.user.role === 'admin' ? 'user' : 'admin'
                        );
                        setActionModal({
                          type: 'role',
                          targetUser: userDetail.user,
                        });
                      }}
                      className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-[#1C1C1C] hover:bg-[#262626] border border-[#262626] text-[#F97316] text-xs font-semibold"
                    >
                      <Shield className="w-3.5 h-3.5" /> Change Role
                    </button>

                    <button
                      onClick={() =>
                        setActionModal({
                          type: 'delete_user',
                          targetUser: userDetail.user,
                        })
                      }
                      className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-red-900/20 hover:bg-red-900/40 border border-red-900/50 text-red-500 text-xs font-semibold"
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Delete User
                    </button>
                    
                    <button
                      onClick={() =>
                        setActionModal({
                          type: 'reset_stats',
                          targetUser: userDetail.user,
                        })
                      }
                      className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-[#1C1C1C] hover:bg-[#262626] border border-[#262626] text-[#E5E5E5] text-xs font-semibold"
                    >
                      <RotateCcw className="w-3.5 h-3.5" /> Reset Stats/Rating
                    </button>

                    <button
                      onClick={() =>
                        setActionModal({
                          type: 'reset_password',
                          targetUser: userDetail.user,
                        })
                      }
                      className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-[#1C1C1C] hover:bg-[#262626] border border-[#262626] text-[#E5E5E5] text-xs font-semibold"
                    >
                      <KeyRound className="w-3.5 h-3.5" /> Password Reset
                    </button>

                    <button
                      onClick={() =>
                        setActionModal({
                          type: 'delete_user',
                          targetUser: userDetail.user,
                        })
                      }
                      className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-red-950/60 hover:bg-red-950/90 border border-red-800/60 text-red-400 text-xs font-bold"
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Delete Account
                    </button>
                  </div>
                </div>

                {/* Recent Games */}
                <div className="space-y-2">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-[#A3A3A3]">
                    Recent Games ({(userDetail.recentGames || []).length})
                  </h4>
                  <div className="bg-[#1C1C1C] border border-[#262626] rounded-xl divide-y divide-[#262626] max-h-44 overflow-y-auto text-xs">
                    {(userDetail.recentGames || []).length === 0 ? (
                      <div className="p-4 text-center text-[#737373]">
                        No games recorded.
                      </div>
                    ) : (
                      (userDetail.recentGames || []).map((g: any, i: number) => (
                        <div key={i} className="p-3 flex justify-between">
                          <span>
                            <strong className="text-[#F5F5F5] uppercase">
                              {g.mode}
                            </strong>{' '}
                            • {g.correctCount}/{g.totalQuestions} correct
                          </span>
                          <span className="font-mono text-[#F97316] font-bold">
                            {g.score} pts
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Feedback & Reports Made */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="bg-[#1C1C1C] border border-[#262626] rounded-xl p-3">
                    <h5 className="text-xs font-bold text-[#E5E5E5] mb-2">
                      Feedback Sent ({(userDetail.feedbackSent || []).length})
                    </h5>
                    <div className="space-y-1.5 max-h-32 overflow-y-auto text-[11px] text-[#A3A3A3]">
                      {(userDetail.feedbackSent || []).map((f: any) => (
                        <div key={f.id} className="truncate">
                          • [{f.category}] {f.message}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="bg-[#1C1C1C] border border-[#262626] rounded-xl p-3">
                    <h5 className="text-xs font-bold text-[#E5E5E5] mb-2">
                      Reports Made ({(userDetail.reportsMade || []).length})
                    </h5>
                    <div className="space-y-1.5 max-h-32 overflow-y-auto text-[11px] text-[#A3A3A3]">
                      {(userDetail.reportsMade || []).map((r: any, idx: number) => (
                        <div key={idx} className="truncate">
                          • {r.reason}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* User Audit History */}
                <div className="space-y-2">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-[#A3A3A3]">
                    Audit History ({(userDetail.auditHistory || []).length})
                  </h4>
                  <div className="bg-[#1C1C1C] border border-[#262626] rounded-xl divide-y divide-[#262626] max-h-44 overflow-y-auto text-xs">
                    {(userDetail.auditHistory || []).length === 0 ? (
                      <div className="p-4 text-center text-[#737373]">
                        No administrative actions on this user.
                      </div>
                    ) : (
                      (userDetail.auditHistory || []).map((log: any) => (
                        <div key={log.id} className="p-2.5 flex justify-between">
                          <span className="font-mono text-[#F97316]">{log.action}</span>
                          <span className="text-[#737373]">
                            {new Date(log.createdAt).toLocaleString()}
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ==================== CONFIRMATION & ACTION MODALS ==================== */}
      {actionModal.type && actionModal.targetUser && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-[#141414] border border-[#262626] rounded-2xl p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-extrabold text-[#F5F5F5] capitalize">
                Confirm {actionModal.type.replace('_', ' ')}
              </h3>
              <button
                onClick={() => setActionModal({ type: null, targetUser: null })}
                className="text-[#A3A3A3] hover:text-[#F5F5F5]"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <p className="text-xs text-[#A3A3A3]">
              Target Scholar:{' '}
              <strong className="text-[#F5F5F5]">
                {actionModal.targetUser.username}
              </strong>{' '}
              ({actionModal.targetUser.email})
            </p>

            {(actionModal.type === 'ban' || actionModal.type === 'suspend') && (
              <div>
                <label className="block text-xs font-bold text-[#A3A3A3] mb-1">
                  Reason for {actionModal.type}
                </label>
                <input
                  type="text"
                  value={actionReason}
                  onChange={(e) => setActionReason(e.target.value)}
                  placeholder="e.g. Violation of community guidelines"
                  className="w-full px-3 py-2 rounded-xl bg-[#0A0A0A] border border-[#262626] text-xs text-[#F5F5F5]"
                />
              </div>
            )}

            {actionModal.type === 'suspend' && (
              <div>
                <label className="block text-xs font-bold text-[#A3A3A3] mb-1">
                  Suspension Duration (Days)
                </label>
                <input
                  type="number"
                  min={1}
                  max={365}
                  value={suspendDays}
                  onChange={(e) => setSuspendDays(Number(e.target.value) || 7)}
                  className="w-full px-3 py-2 rounded-xl bg-[#0A0A0A] border border-[#262626] text-xs text-[#F5F5F5]"
                />
              </div>
            )}

            {actionModal.type === 'role' && (
              <div>
                <label className="block text-xs font-bold text-[#A3A3A3] mb-1">
                  Select New Role
                </label>
                <select
                  value={targetRole}
                  onChange={(e) => setTargetRole(e.target.value as 'user' | 'admin')}
                  className="w-full px-3 py-2 rounded-xl bg-[#0A0A0A] border border-[#262626] text-xs text-[#F5F5F5]"
                >
                  <option value="user">user (Standard Scholar)</option>
                  <option value="admin">admin (Full Administrator)</option>
                </select>
              </div>
            )}

            {actionModal.type === 'delete_user' && (
              <div className="space-y-2 p-3 rounded-xl bg-red-950/30 border border-red-900/50">
                <p className="text-xs text-red-300">
                  This permanently deletes the account, stats, and auth record. Type{' '}
                  <strong className="font-mono text-red-400">
                    {actionModal.targetUser.username}
                  </strong>{' '}
                  below to confirm:
                </p>
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder={actionModal.targetUser.username}
                  className="w-full px-3 py-2 rounded-xl bg-[#0A0A0A] border border-red-900/60 text-xs text-[#F5F5F5]"
                />
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => setActionModal({ type: null, targetUser: null })}
                className="px-4 py-2 rounded-xl bg-[#1C1C1C] text-xs font-semibold text-[#A3A3A3]"
              >
                Cancel
              </button>
              <button
                disabled={submittingAction}
                onClick={handleConfirmUserAction}
                className="px-4 py-2 rounded-xl bg-[#F97316] text-[#0A0A0A] text-xs font-extrabold"
              >
                {submittingAction ? 'Processing...' : 'Confirm Action'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Question Modal */}
      {editingQuestion && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="w-full max-w-lg bg-[#141414] border border-[#262626] rounded-2xl p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-extrabold text-[#F5F5F5]">
                Edit Scripture Question #{editingQuestion.id}
              </h3>
              <button
                onClick={() => setEditingQuestion(null)}
                className="text-[#A3A3A3]"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="space-y-3 text-xs">
              <div>
                <label className="block text-[#A3A3A3] mb-1">Scripture Reference</label>
                <input
                  type="text"
                  value={editingQuestion.scriptureReference || ''}
                  onChange={(e) =>
                    setEditingQuestion({
                      ...editingQuestion,
                      scriptureReference: e.target.value,
                    })
                  }
                  className="w-full px-3 py-2 rounded-xl bg-[#0A0A0A] border border-[#262626] text-[#F5F5F5]"
                />
              </div>
              <div>
                <label className="block text-[#A3A3A3] mb-1">Question Text</label>
                <textarea
                  rows={3}
                  value={editingQuestion.questionText || ''}
                  onChange={(e) =>
                    setEditingQuestion({
                      ...editingQuestion,
                      questionText: e.target.value,
                    })
                  }
                  className="w-full px-3 py-2 rounded-xl bg-[#0A0A0A] border border-[#262626] text-[#F5F5F5]"
                />
              </div>
              <div>
                <label className="block text-[#A3A3A3] mb-1">Correct Answer</label>
                <input
                  type="text"
                  value={editingQuestion.correctAnswer || ''}
                  onChange={(e) =>
                    setEditingQuestion({
                      ...editingQuestion,
                      correctAnswer: e.target.value,
                    })
                  }
                  className="w-full px-3 py-2 rounded-xl bg-[#0A0A0A] border border-[#262626] text-[#10B981]"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setEditingQuestion(null)}
                className="px-4 py-2 rounded-xl bg-[#1C1C1C] text-xs text-[#A3A3A3]"
              >
                Cancel
              </button>
              <button
                onClick={() =>
                  handleQuestionAction('edit', editingQuestion.id, undefined, {
                    scriptureReference: editingQuestion.scriptureReference,
                    questionText: editingQuestion.questionText,
                    correctAnswer: editingQuestion.correctAnswer,
                    options: editingQuestion.options,
                    difficulty: editingQuestion.difficulty,
                    category: editingQuestion.category,
                  })
                }
                className="px-4 py-2 rounded-xl bg-[#F97316] text-[#0A0A0A] text-xs font-extrabold"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Question Confirmation Modal */}
      {deletingQuestion && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-[#141414] border border-[#262626] rounded-2xl p-6 space-y-4">
            <h3 className="text-base font-extrabold text-[#F5F5F5]">
              Delete Question #{deletingQuestion.id}?
            </h3>
            <p className="text-xs text-[#A3A3A3]">
              "{deletingQuestion.questionText}" will be permanently removed from the question bank.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setDeletingQuestion(null)}
                className="px-4 py-2 rounded-xl bg-[#1C1C1C] text-xs text-[#A3A3A3]"
              >
                Cancel
              </button>
              <button
                onClick={() => handleQuestionAction('delete', deletingQuestion.id)}
                className="px-4 py-2 rounded-xl bg-red-600 text-white text-xs font-extrabold"
              >
                Delete Permanently
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reply Feedback by Email Modal */}
      {replyModalFeedback && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-[#141414] border border-[#262626] rounded-2xl p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-extrabold text-[#F5F5F5]">
                Reply by Email (Resend)
              </h3>
              <button
                onClick={() => setReplyModalFeedback(null)}
                className="text-[#A3A3A3]"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-xs text-[#A3A3A3]">
              To: <strong className="text-[#F5F5F5]">{replyModalFeedback.email}</strong>
            </p>
            <textarea
              rows={4}
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              placeholder="Write your response to this scholar..."
              className="w-full px-3 py-2 rounded-xl bg-[#0A0A0A] border border-[#262626] text-xs text-[#F5F5F5]"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setReplyModalFeedback(null)}
                className="px-4 py-2 rounded-xl bg-[#1C1C1C] text-xs text-[#A3A3A3]"
              >
                Cancel
              </button>
              <button
                disabled={!replyText.trim() || submittingAction}
                onClick={() =>
                  handleFeedbackAction(
                    replyModalFeedback.id,
                    'reply_email',
                    'resolved',
                    replyText,
                    replyModalFeedback.email
                  )
                }
                className="px-4 py-2 rounded-xl bg-[#F97316] text-[#0A0A0A] text-xs font-extrabold"
              >
                Send Email & Mark Resolved
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Event Participants Modal */}
      {viewingParticipantsEvent && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-[#141414] border border-[#262626] rounded-2xl p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-extrabold text-[#F5F5F5]">
                Participants — {viewingParticipantsEvent.title}
              </h3>
              <button
                onClick={() => setViewingParticipantsEvent(null)}
                className="text-[#A3A3A3]"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="divide-y divide-[#262626] max-h-72 overflow-y-auto text-xs">
              {(viewingParticipantsEvent.participants || []).length === 0 ? (
                <div className="py-8 text-center text-[#737373]">
                  No participants currently enrolled.
                </div>
              ) : (
                (viewingParticipantsEvent.participants || []).map((p: any) => (
                  <div
                    key={p.uid}
                    className="py-2.5 flex items-center justify-between"
                  >
                    <div>
                      <span className="font-bold text-[#F5F5F5]">{p.username}</span>
                      <span className="text-[#737373] ml-2">({p.country || 'PH'})</span>
                    </div>
                    <button
                      onClick={() => {
                        if (viewingParticipantsEvent.type === 'live') {
                          handleEventAction('remove_live_participant', {
                            roomPin: viewingParticipantsEvent.roomPin,
                            participantUid: p.uid,
                          });
                        } else {
                          handleEventAction('remove_tournament_participant', {
                            eventId: viewingParticipantsEvent.eventId,
                            participantUid: p.uid,
                          });
                        }
                        setViewingParticipantsEvent({
                          ...viewingParticipantsEvent,
                          participants: viewingParticipantsEvent.participants.filter(
                            (x: any) => x.uid !== p.uid
                          ),
                        });
                      }}
                      className="px-2.5 py-1 rounded bg-red-950/50 text-red-400 text-[11px]"
                    >
                      Remove
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Ban Poster Modal */}
      {banPosterModal && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-[#141414] border border-[#262626] rounded-2xl p-6 space-y-4">
            <h3 className="text-base font-extrabold text-[#F5F5F5]">
              Ban Poster: {banPosterModal.username}
            </h3>
            <input
              type="text"
              value={banPosterReason}
              onChange={(e) => setBanPosterReason(e.target.value)}
              placeholder="Reason for banning poster..."
              className="w-full px-3 py-2 rounded-xl bg-[#0A0A0A] border border-[#262626] text-xs text-[#F5F5F5]"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setBanPosterModal(null)}
                className="px-4 py-2 rounded-xl bg-[#1C1C1C] text-xs text-[#A3A3A3]"
              >
                Cancel
              </button>
              <button
                onClick={() =>
                  handleFeedAction(
                    'ban_poster',
                    banPosterModal.postId,
                    banPosterModal.posterUid,
                    banPosterReason
                  )
                }
                className="px-4 py-2 rounded-xl bg-red-600 text-white text-xs font-extrabold"
              >
                Ban & Remove Post
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Book Chapter-Level Distribution Modal */}
      {selectedBookDetail && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="w-full max-w-xl bg-[#141414] border border-[#262626] rounded-2xl p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-base font-extrabold text-[#F5F5F5]">
                  {selectedBookDetail.bookName} — Chapter-Level Coverage
                </h3>
                <p className="text-xs text-[#A3A3A3]">
                  {selectedBookDetail.testament} • {selectedBookDetail.chaptersCoveredCount} of{' '}
                  {selectedBookDetail.totalChapters} chapters covered
                </p>
              </div>
              <button
                onClick={() => setSelectedBookDetail(null)}
                className="text-[#A3A3A3] hover:text-[#F5F5F5]"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="grid grid-cols-5 sm:grid-cols-8 gap-2 max-h-72 overflow-y-auto p-1 text-xs">
              {Array.from({ length: selectedBookDetail.totalChapters }, (_, idx) => {
                const ch = idx + 1;
                const count =
                  selectedBookDetail.chapterDistribution?.[String(ch)] || 0;
                return (
                  <div
                    key={ch}
                    className={`p-2 rounded-xl border text-center font-mono ${
                      count > 0
                        ? 'bg-[#10B981]/15 border-[#10B981]/40 text-[#10B981]'
                        : 'bg-[#0A0A0A] border-[#262626] text-[#737373]'
                    }`}
                  >
                    <div className="text-[10px]">Ch {ch}</div>
                    <div className="font-extrabold">{count}</div>
                  </div>
                );
              })}
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={() => setSelectedBookDetail(null)}
                className="px-4 py-2 rounded-xl bg-[#1C1C1C] text-xs text-[#A3A3A3]"
              >
                Close
              </button>
              <button
                disabled={qbBusy}
                onClick={() => {
                  const targetBook = selectedBookDetail.bookName;
                  setSelectedBookDetail(null);
                  handleGenerateQbBatch(targetBook);
                }}
                className="px-4 py-2 rounded-xl bg-[#F97316] text-[#0A0A0A] text-xs font-extrabold"
              >
                Generate More for {selectedBookDetail.bookName}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Inspect Question Verification Details Modal */}
      {inspectingQuestion && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="w-full max-w-lg bg-[#141414] border border-[#262626] rounded-2xl p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-extrabold text-[#F5F5F5]">
                Verification Details — Question #{inspectingQuestion.id}
              </h3>
              <button
                onClick={() => setInspectingQuestion(null)}
                className="text-[#A3A3A3]"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-3 text-xs">
              <div className="p-3 rounded-xl bg-[#1C1C1C] border border-[#262626] space-y-1">
                <div className="flex justify-between">
                  <span className="font-mono font-bold text-[#F97316]">
                    {inspectingQuestion.scriptureReference}
                  </span>
                  <span className="uppercase font-bold text-[#10B981]">
                    Status: {inspectingQuestion.validationStatus}
                  </span>
                </div>
                <div className="text-[#A3A3A3]">
                  Book: {inspectingQuestion.book} • Category: {inspectingQuestion.category} •
                  Source: World English Bible (WEB)
                </div>
              </div>

              <div>
                <div className="text-[#737373] uppercase text-[10px] font-bold mb-1">
                  Question Text
                </div>
                <div className="p-3 rounded-xl bg-[#0A0A0A] border border-[#262626] text-[#F5F5F5]">
                  {inspectingQuestion.questionText}
                </div>
              </div>

              <div>
                <div className="text-[#737373] uppercase text-[10px] font-bold mb-1">
                  Verified Answer & Explanation
                </div>
                <div className="p-3 rounded-xl bg-[#0A0A0A] border border-[#262626] space-y-1">
                  <div className="font-bold text-[#10B981]">
                    Correct: {inspectingQuestion.correctAnswer}
                  </div>
                  <div className="text-[#A3A3A3]">{inspectingQuestion.explanation}</div>
                </div>
              </div>

              <div>
                <div className="text-[#737373] uppercase text-[10px] font-bold mb-1">
                  11-Step Verification Notes
                </div>
                <div className="p-3 rounded-xl bg-[#0A0A0A] border border-[#262626] font-mono text-[11px] text-[#E5E5E5]">
                  {inspectingQuestion.verificationNotes ||
                    'Verified against canonical World English Bible (WEB) scripture text, 4 distinct options, and duplicate canonical key guard.'}
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                onClick={() => setInspectingQuestion(null)}
                className="px-4 py-2 rounded-xl bg-[#1C1C1C] text-xs text-[#A3A3A3]"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
